import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  FileText, Loader2, Check, Trash2, ChevronDown, ChevronUp, ChevronLeft,
  ChevronRight, ArrowRight, Bell, LayoutGrid, Video, FolderOpen, Settings2,
  ExternalLink, Download, Plus, X, Link as LinkIcon, ListChecks, Users,
  Mail, Phone, UserPlus,
} from "lucide-react";
// One definition of the bank details, shared with every other client-facing
// payment surface, so they can never drift between modules.
import { WIRE_INSTRUCTIONS, WIRE_ROWS } from "@/lib/wireInstructions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const cn = (...c) => c.filter(Boolean).join(" ");

/*
 * The client project dashboard — the evolution of the single-scroll tracker
 * into a small portal: Dashboard / Meetings / Documents / Settings, with a
 * right rail carrying the next meeting, the project's key figures and a
 * calendar of everything that has happened (and is booked to happen).
 *
 * Everything renders on the global VEXOS system: warm paper, hairlines instead
 * of boxes, weight-300 numerals as heroes, estate green as the only accent.
 * The layout borrows from SaaS dashboards; the voice and surfaces do not.
 */

/* ── Geometry ─────────────────────────────────────────────────────────────── */
/* Corners soften as surfaces nest. Three steps and no magic numbers: panels
   that sit on the page, wells nested inside a panel, chips inside those.
   Anything that reads as a control — buttons, badges, monograms, dots — stays
   fully round. The page runs softer than the system's 12px card on purpose:
   this is the one surface a client sees, and it should feel hospitable. */
const R = {
  panel: 18,
  well: 12,
  chip: 8,
};

/* ── Viewer identity (no client accounts — the link is the access) ─────────── */
/* Whoever is looking at the page can tell us who they are; it personalises the
   greeting and prefills nothing else. Stored locally, never required. */

const profileKey = (token) => `trp-tracker-profile:${token}`;
const seenKey = (token) => `trp-tracker-seen:${token}`;

function loadProfile(token) {
  try { return JSON.parse(localStorage.getItem(profileKey(token))) || {}; }
  catch { return {}; }
}
function saveProfile(token, profile) {
  try { localStorage.setItem(profileKey(token), JSON.stringify(profile)); } catch { /* private mode */ }
}

/* ── Small brand primitives ───────────────────────────────────────────────── */

// Scroll reveal: sections fade up 18px as they ENTER the viewport, siblings
// staggered 90ms. An animation-delay alone fires everything on load, so this
// observes instead — the brand's motion spec is about arrival, not a load splash.
function Reveal({ index = 0, className, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      // threshold must stay 0: a percentage threshold is impossible to meet for
      // a block taller than the viewport (the 44-item visualization list runs
      // ~7000px, and 8% of that never fits on screen), so those sections would
      // sit at opacity 0 forever. The negative rootMargin is what gives the
      // reveal its "arriving" feel — the threshold was never doing that work.
      { rootMargin: "0px 0px -12% 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={cn("reveal-on-enter", className)}
      style={{ "--reveal-stagger": `${(index % 4) * 90}ms` }}>
      {children}
    </div>
  );
}

// A titled section. Structure comes from a rule and whitespace, not a box.
//
// `tone="ink"` is the one exception, and it exists for VEXOS. The platform's
// sections used to differ from the visualization ones by a shade of numeral,
// which at arm's length is no difference at all: two three-column blocks with
// the same numerals and the same rules read as the same section twice. On ink
// they cannot be confused. The panel replaces the rule it would otherwise
// draw — a black block on warm paper is already a separation.
function Section({ label, title, note, right, index, tight, tone, children }) {
  const ink = tone === "ink";
  return (
    <Reveal index={index}>
      <section
        className={cn(ink && "ink-section")}
        style={{
          marginTop: tight ? 40 : 52,
          ...(ink ? {
            borderRadius: R.panel,
            padding: "clamp(26px, 3vw, 38px) clamp(22px, 2.6vw, 34px)",
            boxShadow: "0 24px 48px -32px rgba(0,0,0,.55)",
          } : null),
        }}>
        {!ink && <hr className="hairline-rule" />}
        <div className="flex items-start justify-between gap-6"
          style={{ paddingTop: ink ? 0 : 20, marginBottom: 24 }}>
          <div className="min-w-0">
            {label && <p className="micro-label" style={{ marginBottom: 10 }}>{label}</p>}
            <h2 className="heading-2">{title}</h2>
            {note && <p className="body-text" style={{ marginTop: 8, maxWidth: "56ch" }}>{note}</p>}
          </div>
          {right}
        </div>
        {children}
      </section>
    </Reveal>
  );
}

/* ── The phase rail ───────────────────────────────────────────────────────── */

const RAIL_Y = 11;
const DOT_H = 6;

function PhaseRail({ phases, onHold }) {
  const n = phases.length;
  const activeIdx = Math.max(0, phases.findIndex((p) => p.state === "current" || p.state === "paused"));
  const inset = n ? 50 / n : 0;
  const span = 100 - 2 * inset;
  const travelled = n > 1 ? (span * activeIdx) / (n - 1) : 0;

  return (
    <div>
      {/* Desktop: a continuous hairline with the travelled portion in estate green */}
      <div className="hidden sm:block">
        <div className="relative" style={{ height: 24 }}>
          <div className="absolute" style={{
            left: `${inset}%`, width: `${span}%`, top: RAIL_Y,
            borderTop: "1px solid var(--hairline-strong)",
          }} />
          <div className="absolute" style={{
            left: `${inset}%`, width: `${travelled}%`, top: RAIL_Y,
            borderTop: `1px solid ${onHold ? "var(--warm-label)" : "var(--estate)"}`,
            transition: "width .55s var(--ease-brand)",
          }} />
          <div className="absolute inset-0 flex items-start" style={{ paddingTop: RAIL_Y - DOT_H / 2 }}>
            {phases.map((p) => (
              <div key={p.key} className="flex-1 flex justify-center">
                <PhaseDot state={p.state} onHold={onHold} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex" style={{ marginTop: 14 }}>
          {phases.map((p) => (
            <div key={p.key} className="flex-1 text-center px-1">
              <PhaseLabel phase={p} onHold={onHold} />
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: hairline-separated rows — a 5-column rail is unreadable at 375px */}
      <div className="sm:hidden">
        {phases.map((p, i) => (
          <div key={p.key} className="flex items-start gap-4"
            style={{ paddingTop: i === 0 ? 0 : 16, paddingBottom: 16, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div style={{ paddingTop: 4 }}><PhaseDot state={p.state} onHold={onHold} /></div>
            <div className="min-w-0 flex-1">
              <PhaseLabel phase={p} onHold={onHold} align="left" />
              {(p.state === "current" || p.state === "paused") && (
                <p className="body-text" style={{ marginTop: 8 }}>{p.blurb}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhaseDot({ state, onHold }) {
  const done = state === "done";
  const active = state === "current" || state === "paused";
  const color = state === "paused" || (active && onHold) ? "var(--warm-label)"
    : done ? "var(--estate)"
    : active ? "var(--estate)"
    : "var(--handle)";
  return (
    <span className={cn("progress-dot", active && "is-active")}
      style={{ backgroundColor: color, display: "block" }} />
  );
}

function PhaseLabel({ phase, onHold, align = "center" }) {
  const done = phase.state === "done";
  const active = phase.state === "current" || phase.state === "paused";
  const paused = phase.state === "paused";
  return (
    <div style={{ textAlign: align }}>
      <p style={{
        fontSize: 13.5,
        fontWeight: active ? 400 : 300,
        letterSpacing: "-0.1px",
        color: paused ? "var(--warm-dark)"
          : active ? "var(--estate)"
          : done ? "var(--ink)"
          : "var(--quiet)",
      }}>
        {phase.label}
      </p>
      {phase.entered_text && (
        <p className="micro-label" style={{ marginTop: 5, color: "var(--quiet)" }}>{phase.entered_text}</p>
      )}
      {paused && (
        <p className="micro-label" style={{ marginTop: 5, color: "var(--warm-label)" }}>paused here</p>
      )}
      {active && !paused && !onHold && (
        <span className="inline-flex items-center gap-1.5" style={{ marginTop: 6 }}>
          <span className="live-dot" />
          <span className="micro-label" style={{ color: "var(--estate)" }}>now</span>
        </span>
      )}
    </div>
  );
}

/* ── V1 → V2 → Final ──────────────────────────────────────────────────────── */

// Same two palettes as the rail above (see RAIL_TONE): green for the renders,
// ink for the platform. Carried down here so the VEXOS section reads as the
// same production as its rail rather than reverting to the green axis halfway
// down the page.
const PROCESS_TONE = {
  viz: {
    figureLive: "var(--sage)",
    figureIdle: "var(--handle)",
    approved: "var(--estate)",
    delivered: "var(--ink)",
    pending: "var(--quiet)",
    stamp: "var(--quiet)",
    noteBorder: "var(--handle)",
    noteColor: "var(--warm-dark)",
    litBg: "var(--selected-tint)",
    litRing: "inset 0 0 0 1px rgba(61,99,85,.22)",
    link: "link-estate",
  },
  // The VEXOS section sits ON ink (Section tone="ink"), so every value here is
  // the inverted one. The typography utilities invert themselves inside
  // .ink-section; these are the colours set inline, which cannot.
  vexos: {
    figureLive: "rgba(255,255,255,.66)",
    figureIdle: "rgba(255,255,255,.22)",
    approved: "#FFFFFF",
    delivered: "#FFFFFF",
    pending: "rgba(255,255,255,.42)",
    stamp: "rgba(255,255,255,.4)",
    noteBorder: "var(--hairline-invert)",
    noteColor: "rgba(255,255,255,.66)",
    litBg: "rgba(255,255,255,.07)",
    litRing: "inset 0 0 0 1px rgba(255,255,255,.2)",
    link: "link-ink",
  },
};

// Static class strings — Tailwind only ships classes it can see written out.
// Four deliveries (the clay rail) go two-by-two: the content column tops out
// around 760px even on a wide screen, and four-across leaves each blurb ~130px.
const PROCESS_GRID = {
  3: "cols-hairline grid sm:grid-cols-3",
  4: "cols-hairline-4 grid sm:grid-cols-2",
};

function DeliveryProcess({ rounds, highlight, openLabel = "Review it now", variant = "viz" }) {
  const t = PROCESS_TONE[variant] || PROCESS_TONE.viz;
  return (
    <div className={PROCESS_GRID[rounds.length] || PROCESS_GRID[3]}>
      {rounds.map((r, i) => {
        const approved = r.status === "approved";
        const delivered = r.status === "delivered";
        // `id` is the landing point for the button in a delivery email: the
        // client arrives ON their round rather than at the top of the page
        // having to work out which of three is the one they were told about.
        const lit = highlight === r.key;
        return (
          <div key={r.key} id={`delivery-${r.key}`}
            style={{
              scrollMarginTop: 96,
              transition: "background-color .6s var(--ease-brand), box-shadow .6s var(--ease-brand)",
              ...(lit ? {
                borderRadius: R.panel,
                padding: "18px 20px",
                margin: "-18px -20px",
                backgroundColor: t.litBg,
                boxShadow: t.litRing,
              } : null),
            }}>
            <p className="editorial-figure" style={{ color: approved || delivered ? t.figureLive : t.figureIdle }}>
              {String(i + 1).padStart(2, "0")}
            </p>
            <p className="micro-label" style={{ marginTop: 16 }}>{r.label} · {r.title}</p>
            <p style={{
              marginTop: 10, fontSize: 13.5, fontWeight: 400, letterSpacing: "-0.1px",
              color: approved ? t.approved : delivered ? t.delivered : t.pending,
            }}>
              {approved && <Check className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" strokeWidth={1.75} />}
              {r.status_text}
              {r.delivered_text && (
                <span className="tabular" style={{ color: t.stamp, fontWeight: 300 }}> · {r.delivered_text}</span>
              )}
            </p>
            <p className="body-text" style={{ marginTop: 12 }}>{r.blurb}</p>
            {r.note && (
              <p className="body-text" style={{
                marginTop: 14, paddingLeft: 14, borderLeft: `1px solid ${t.noteBorder}`,
                color: t.noteColor,
              }}>{r.note}</p>
            )}
            {/* One link opens as the round's own button. Several — orbit,
                stills, 360s, often per unit — are listed by what they are, so
                the client can tell which one they're reviewing. */}
            {(r.links || []).length > 1 ? (
              <ul style={{ marginTop: 16 }} className="space-y-2">
                {r.links.map((l) => (
                  <li key={l.id || l.url}>
                    <a className={t.link} href={l.url} target="_blank" rel="noreferrer">
                      {l.display_label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : r.url && (
              <p style={{ marginTop: 16 }}>
                <a className={t.link} href={r.url} target="_blank" rel="noreferrer">
                  {approved ? "View" : openLabel}
                </a>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── The hero banner ──────────────────────────────────────────────────────── */
/*
 * ONE banner, chosen server-side (services/payment_dunning.build_banner), sat
 * above everything on every page of the portal. The client should never have to
 * find the payments section to learn that a payment is coming, is late, or has
 * paused their project — that is the whole reason this exists.
 *
 * The tone ladder is deliberately steep at the top and quiet at the bottom.
 * Terracotta is the brand's only alert colour, and if the seven-day notice
 * already shouts there is nothing left to escalate with at twenty-one. So:
 * a forecast is estate green, a due date is warm, past due is warm terracotta,
 * and only a genuinely paused project gets the solid inverted panel.
 */

const BANNER_TONE = {
  info:     { bg: "var(--selected-tint)", border: "rgba(61,99,85,.18)",  fg: "var(--estate)",      accent: "var(--estate)" },
  notice:   { bg: "var(--warm-badge)",    border: "rgba(74,66,53,.16)",  fg: "var(--warm-dark)",   accent: "var(--warm-dark)" },
  warning:  { bg: "var(--warm-badge)",    border: "rgba(185,106,67,.28)", fg: "var(--warm-dark)",  accent: "#B96A43" },
  serious:  { bg: "#FAEDE9",              border: "rgba(194,74,46,.26)", fg: "var(--terracotta)",  accent: "var(--terracotta)" },
  critical: { bg: "#FAEDE9",              border: "rgba(194,74,46,.34)", fg: "var(--terracotta)",  accent: "var(--terracotta)" },
};

function HeroBanner({ banner, onPay, paying }) {
  if (!banner) return null;
  const paused = banner.kind === "paused";
  const tone = BANNER_TONE[banner.severity] || BANNER_TONE.info;

  // A paused project gets the one inverted surface on the page. It is the only
  // state where the client genuinely cannot proceed, and it should not be
  // possible to skim past it.
  const surface = paused
    ? { backgroundColor: "var(--terracotta)", border: "1px solid var(--terracotta)" }
    : { backgroundColor: tone.bg, border: `1px solid ${tone.border}` };
  const titleColor = paused ? "#FFFFFF" : tone.fg;
  const bodyColor = paused ? "rgba(255,255,255,.86)" : tone.fg;
  const detailColor = paused ? "rgba(255,255,255,.66)" : "var(--tertiary)";

  return (
    <div className="animate-reveal-up" style={{
      ...surface, borderRadius: R.panel, padding: "24px 28px", marginBottom: 30,
    }}>
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-5">
        <div className="min-w-0" style={{ flex: "1 1 380px" }}>
          <div className="flex items-center gap-2.5" style={{ marginBottom: 12 }}>
            <span style={{
              width: 6, height: 6, borderRadius: 999, flexShrink: 0,
              backgroundColor: paused ? "#FFFFFF" : tone.accent,
            }} />
            <span className="micro-label" style={{ color: paused ? "rgba(255,255,255,.62)" : tone.accent }}>
              {paused ? "Project paused"
                : banner.kind === "forecast" ? "Coming up"
                : banner.kind === "due_soon" ? "Upcoming payment"
                : "Payment past due"}
            </span>
          </div>

          <p style={{
            fontSize: "clamp(19px, 2vw, 23px)", fontWeight: 300, letterSpacing: "-0.6px",
            lineHeight: 1.22, color: titleColor,
          }}>
            {banner.title}
          </p>

          <p className="body-text" style={{ marginTop: 12, maxWidth: "68ch", color: bodyColor }}>
            {banner.body}
          </p>

          {banner.detail && (
            <p className="micro-label" style={{ marginTop: 14, color: detailColor }}>
              {banner.detail}
            </p>
          )}
        </div>

        {/* The amount and the way out, together. A client who has just read that
            they owe money should not then have to go looking for how to pay it. */}
        {(banner.amount_text || banner.can_pay) && (
          <div className="shrink-0" style={{ textAlign: "right" }}>
            {banner.amount_text && (
              <p className="tabular" style={{
                fontSize: 30, fontWeight: 300, letterSpacing: "-1px", lineHeight: 1.1,
                color: titleColor,
              }}>
                {banner.amount_text}
              </p>
            )}
            {banner.can_pay && (
              <button
                onClick={() => onPay?.(banner.milestone_id)}
                disabled={paying}
                className="inline-flex items-center gap-2"
                style={{
                  marginTop: banner.amount_text ? 16 : 0,
                  padding: "10px 24px", borderRadius: 999,
                  backgroundColor: paused ? "#FFFFFF" : "var(--ink)",
                  color: paused ? "var(--terracotta)" : "#FFFFFF",
                  fontSize: 14, fontWeight: 400, letterSpacing: "-0.25px",
                  opacity: paying ? 0.6 : 1, cursor: paying ? "default" : "pointer",
                }}>
                {paying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {paying ? "Opening" : "Pay this milestone"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Paying a milestone ───────────────────────────────────────────────────── */
/*
 * Wire first, card second, and the 3% stated before the client commits to
 * anything. On a five-figure milestone that surcharge is real money, and a
 * client who discovers it on Stripe's confirmation screen has been ambushed by
 * us — which is a strange way to treat someone in the act of paying.
 */

function PayDialog({ milestone, token, onClose, onPaid }) {
  const [mode, setMode] = useState(null);      // null | "card" | "wire"
  const [busy, setBusy] = useState(false);

  const startCard = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/project-tracker/client/${token}/milestones/${milestone.id}/checkout`,
        { origin_url: window.location.origin },
      );
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't open the payment page. Please try wire, or tell us.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ padding: 20 }}>
      <div onClick={onClose} className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,.34)" }} />
      <div className="relative w-full overflow-y-auto" style={{
        maxWidth: 520, maxHeight: "88vh", backgroundColor: "var(--surface)",
        borderRadius: R.panel, border: "1px solid var(--hairline)", padding: "30px 32px",
      }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="micro-label" style={{ marginBottom: 10 }}>Settle a payment</p>
            <p style={{ fontSize: 20, fontWeight: 300, letterSpacing: "-0.5px", color: "var(--ink)" }}>
              {milestone.title}
            </p>
          </div>
          <button onClick={onClose} style={{ color: "var(--quiet)" }} aria-label="Close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <p className="tabular" style={{
          marginTop: 20, fontSize: 34, fontWeight: 300, letterSpacing: "-1.2px", color: "var(--ink)",
        }}>
          {milestone.escalation?.outstanding_text || milestone.amount_text}
        </p>
        {milestone.due_text && (
          <p className="micro-label" style={{ marginTop: 8 }}>Due {milestone.due_text}</p>
        )}

        {!mode && (
          <div style={{ marginTop: 28 }}>
            {/* Wire leads. It costs the client nothing and it costs us nothing. */}
            <button onClick={() => setMode("wire")} className="w-full text-left"
              style={{
                padding: "18px 20px", borderRadius: R.well, border: "1px solid var(--hairline)",
                backgroundColor: "var(--image-well)",
              }}>
              <div className="flex items-center justify-between gap-3">
                <p style={{ fontSize: 15, fontWeight: 400, color: "var(--ink)" }}>Pay by wire or ACH</p>
                <span className="micro-label" style={{ color: "var(--estate)" }}>No fee</span>
              </div>
              <p className="body-text" style={{ marginTop: 6 }}>
                Our bank details, ready to hand to your accounts team.
              </p>
            </button>

            <button onClick={startCard} disabled={busy} className="w-full text-left"
              style={{
                marginTop: 12, padding: "18px 20px", borderRadius: R.well,
                border: "1px solid var(--hairline)", backgroundColor: "var(--surface)",
                opacity: busy ? 0.6 : 1,
              }}>
              <div className="flex items-center justify-between gap-3">
                <p style={{ fontSize: 15, fontWeight: 400, color: "var(--ink)" }}>
                  {busy ? "Opening secure checkout" : "Pay by card"}
                </p>
                <span className="micro-label">+3% processing</span>
              </div>
              <p className="body-text" style={{ marginTop: 6 }}>
                Instant, via Stripe. A 3% processing fee is added at checkout.
              </p>
            </button>
          </div>
        )}

        {mode === "wire" && (
          <div style={{ marginTop: 26 }}>
            <div style={{
              padding: "18px 20px", borderRadius: R.well, backgroundColor: "var(--image-well)",
              border: "1px solid var(--hairline)",
            }}>
              {WIRE_ROWS.map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4"
                  style={{ padding: "7px 0", fontSize: 13.5 }}>
                  <span style={{ color: "var(--tertiary)", flexShrink: 0 }}>{label}</span>
                  <span className="tabular" style={{ color: "var(--ink)", textAlign: "right" }}>{value}</span>
                </div>
              ))}
            </div>
            <p className="body-text" style={{ marginTop: 16 }}>
              Please reference <strong style={{ fontWeight: 400, color: "var(--ink)" }}>{milestone.title}</strong> on
              the transfer, and email {WIRE_INSTRUCTIONS.contact} with the confirmation so we can match it
              the same day.
            </p>
            <button onClick={() => setMode(null)} className="micro-label"
              style={{ marginTop: 18, color: "var(--tertiary)" }}>
              ← Other ways to pay
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Dashboard building blocks ────────────────────────────────────────────── */

// The one tinted surface on the page. Warm for "we're waiting on something",
// green tint for a positive confirmation. Hairline, never a shadow.
function Notice({ tone, title, children }) {
  const warm = tone === "warm";
  return (
    <div style={{
      marginTop: 32,
      padding: "24px 26px",
      borderRadius: R.panel,
      backgroundColor: warm ? "var(--warm-badge)" : "var(--selected-tint)",
      border: `1px solid ${warm ? "rgba(74,66,53,.14)" : "rgba(61,99,85,.16)"}`,
    }}>
      <p style={{
        fontSize: 17, fontWeight: 300, letterSpacing: "-0.3px",
        color: warm ? "var(--warm-dark)" : "var(--estate)",
      }}>{title}</p>
      <div className="body-text" style={{
        marginTop: 10, maxWidth: "66ch",
        color: warm ? "var(--warm-dark)" : "var(--estate)",
      }}>{children}</div>
    </div>
  );
}

// A thin progress arc — the mockup's donut, restated in hairline precision.
function ProgressRing({ percent = 0, size = 172 }) {
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, percent)) / 100;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="var(--track-fill)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="var(--estate)" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${c * filled} ${c}`}
          style={{ transition: "stroke-dasharray .85s var(--ease-brand)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="hero-numeral tabular" style={{ fontSize: 44 }}>{percent}%</p>
        <p className="micro-label" style={{ marginTop: 6 }}>Complete</p>
      </div>
    </div>
  );
}

// One figure with its caps micro-label beneath — the brand's numeral treatment.
// `tint` gives the tile one of the mockup's soft pastel fills, on-palette.
const TILE_TINTS = {
  warm: { backgroundColor: "var(--warm-badge)", border: "1px solid rgba(74,66,53,.10)" },
  green: { backgroundColor: "var(--selected-tint)", border: "1px solid rgba(61,99,85,.12)" },
  well: { backgroundColor: "var(--image-well)", border: "1px solid var(--hairline)" },
  plain: { backgroundColor: "var(--surface)", border: "1px solid var(--hairline)" },
};

/* The four numbers that describe where a project stands. ONE source, because
   they appear twice — as compact rows in the left rail on a wide screen, and as
   tiles on a phone, where there is no left rail to put them in. Two copies of
   this arithmetic is two answers to "how many files are in". */
function projectStats(tracker) {
  const files = tracker.files || {};
  const rounds = tracker.rounds || [];
  const meetings = tracker.meetings || [];
  const approvedRounds = rounds.filter((r) => r.status === "approved").length;
  const daysIn = (() => {
    if (!tracker.timeline_started_at) return null;
    const d = Math.floor((Date.now() - new Date(tracker.timeline_started_at).getTime()) / 86400000);
    return d >= 0 ? d : null;
  })();

  return [
    {
      key: "files", tint: "warm", label: "Files in",
      value: files.has_checklist ? `${files.required_complete ?? 0}/${files.required_total ?? 0}` : "—",
      detail: !files.has_checklist ? "List being prepared"
        : files.files_complete ? "All collected"
        : `${files.outstanding_required ?? 0} outstanding`,
    },
    // Counts V1/V2/Final — three DELIVERIES. Never label this "rounds": the
    // client gets two rounds of revision, not three.
    { key: "rounds", tint: "green", label: "Deliveries approved",
      value: `${approvedRounds}/${rounds.length || 3}`, detail: null },
    { key: "meetings", tint: "well", label: "Meetings held", value: meetings.length, detail: null },
    {
      key: "clock", tint: "plain",
      value: daysIn === null ? "—" : daysIn,
      label: daysIn !== null ? "Days in" : tracker.clock_running ? "Under way" : "Not started",
      detail: daysIn !== null && tracker.eta_weeks_text ? `of ${tracker.eta_weeks_text}` : null,
    },
  ];
}

function StatTile({ value, label, detail, tint = "plain" }) {
  return (
    <div style={{ ...TILE_TINTS[tint], borderRadius: R.panel, padding: "20px 20px 18px" }}>
      <p className="tabular" style={{ fontSize: 30, fontWeight: 300, letterSpacing: "-1px", color: "var(--ink)", lineHeight: 1 }}>
        {value}
      </p>
      <p className="micro-label" style={{ marginTop: 10 }}>{label}</p>
      {detail && <p className="micro-label" style={{ marginTop: 5, color: "var(--quiet)" }}>{detail}</p>}
    </div>
  );
}

/* A face where we have one, a monogram where we don't.
 *
 * `photo_url` comes back from the API as an app-relative path (the staff photo
 * endpoint, or the bundled headshot route) — both public, so the client's
 * browser fetches them with no token. A photo that 404s or is simply missing
 * falls back to the monogram this page drew for everyone before, rather than
 * leaving a broken-image glyph on a client's dashboard. */
const assetUrl = (path) =>
  !path ? null : /^https?:/.test(path) ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;

function Avatar({ person, size = 54, className, style }) {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : assetUrl(person?.photo_url);
  const initials =
    person?.initials || (person?.name || "?").trim().slice(0, 2).toUpperCase();
  const box = {
    width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.26)),
    ...style,
  };
  if (src) {
    return (
      <img
        src={src}
        alt={person?.name || ""}
        onError={() => setFailed(true)}
        className={cn("rounded-full shrink-0 object-cover", className)}
        style={{ ...box, border: "1px solid var(--hairline-strong)" }}
      />
    );
  }
  return (
    <span className={cn("monogram shrink-0", className)}
      style={{ ...box, letterSpacing: size < 36 ? "0.8px" : "1.4px" }}>
      {initials}
    </span>
  );
}

// An avatar cluster. Faces where we have them; initials in a hairline ring
// where we don't, which reads like a letterhead rather than a gap.
function MonogramRow({ people, max = 4 }) {
  const shown = (people || []).slice(0, max);
  const extra = (people || []).length - shown.length;
  if (!shown.length) return null;
  return (
    <div className="flex items-center">
      {shown.map((p, i) => (
        <span key={p.id || p.key || p.name || i} title={p.name}
          style={{ marginLeft: i === 0 ? 0 : -8, display: "inline-flex" }}>
          <Avatar person={p} size={30}
            style={{ backgroundColor: "var(--image-well)", color: "var(--body)" }} />
        </span>
      ))}
      {extra > 0 && (
        <span className="micro-label" style={{ marginLeft: 8 }}>+{extra}</span>
      )}
    </div>
  );
}

/* ── Where you are in the delivery ────────────────────────────────────────── */

// THREE DELIVERIES, TWO ROUNDS OF REVISION (see routers/project_tracker.py).
// The rail states which of the three you're looking at right now: the current
// delivery carries a tinted ground and a magnified numeral, the ones behind it
// are checked and quiet, the ones ahead quieter still. This is a different axis
// from the Phases rail lower down — that one is the project's life, this one is
// the delivery you're being asked to review.
// Where the client's attention belongs. A delivery sitting with them for
// review outranks one we haven't sent yet, so that wins over the next pending
// round. Everything approved → rest on the last one.
function currentRoundIndex(rounds) {
  const withClient = rounds.findIndex((r) => r.status === "delivered");
  if (withClient !== -1) return withClient;
  const next = rounds.findIndex((r) => r.status === "pending");
  return next === -1 ? rounds.length - 1 : next;
}

// TWO PRODUCTIONS, TWO PALETTES.
// Visualization runs on the brand's estate green — the accent the rest of the
// page already uses. VEXOS runs on the black axis: an ink ground under the live
// round, graphite either side of it, and no green anywhere. Same geometry, same
// rhythm, same rules — only the tone changes, so the client can tell which of
// the two productions they are reading before they read the heading.
const RAIL_TONE = {
  viz: {
    activeBg: "var(--selected-tint)",
    activeBorder: "rgba(61,99,85,.16)",
    activeShadow: "none",
    activeLabel: "var(--estate)",
    activeTitle: "var(--body-dark)",
    activeStatus: "var(--estate)",
    activeDot: "var(--estate)",
    doneCheck: "var(--estate)",
    doneLabel: "var(--body-dark)",
    doneStatus: "var(--estate)",
    idleDot: "var(--handle)",
  },
  vexos: {
    activeBg: "var(--ink)",
    activeBorder: "var(--ink)",
    // The one lift on the page. A black cell on warm paper wants a shadow to
    // sit ON the paper rather than be a hole cut in it — long, low and almost
    // invisible, in the brand's own ink rather than a grey.
    activeShadow: "0 16px 34px -22px rgba(0,0,0,.6)",
    activeLabel: "#FFFFFF",
    activeTitle: "rgba(255,255,255,.7)",
    activeStatus: "rgba(255,255,255,.5)",
    activeDot: "rgba(255,255,255,.88)",
    doneCheck: "var(--ink)",
    doneLabel: "var(--body-dark)",
    doneStatus: "var(--tertiary)",
    idleDot: "var(--disabled)",
  },
};

function DeliveryRail({ rounds, variant = "viz" }) {
  if (!rounds?.length) return null;
  const current = currentRoundIndex(rounds);
  const t = RAIL_TONE[variant] || RAIL_TONE.viz;

  return (
    /* Three (or, on the clay rail, four) across on a real screen. On a phone
       the columns would be ~110px — narrow enough that "With you for review"
       breaks over three lines — so the rail stacks into rows and each delivery
       reads on one line instead. */
    <div className={rounds.length === 4 ? "grid grid-cols-1 sm:grid-cols-4" : "grid grid-cols-1 sm:grid-cols-3"}
      style={{ gap: 6 }}>
      {rounds.map((r, i) => {
        const done = r.status === "approved";
        const active = i === current;
        // Only the active cell takes a ground; the rest stay on the paper so
        // the page still reads as whitespace and rules, not a row of boxes.
        return (
          /* On a phone the three parts sit on one line and the title wraps
             beneath them. It used to share that line and be truncated, which
             on the VEXOS rail — where the label is "VEXOS Milestone Two",
             not "V2" — left the client reading "R..". */
          <div key={r.key}
            className={cn(
              "flex flex-wrap items-center gap-x-3 gap-y-1 sm:block",
              active ? "px-4 py-3 sm:px-5 sm:pt-[18px] sm:pb-4" : "px-4 py-3 sm:px-1 sm:pt-[18px] sm:pb-4",
            )}
            style={{
              borderRadius: R.panel,
              backgroundColor: active ? t.activeBg : "transparent",
              border: `1px solid ${active ? t.activeBorder : "transparent"}`,
              boxShadow: active ? t.activeShadow : "none",
              transition: "background-color .4s var(--ease-brand), box-shadow .4s var(--ease-brand)",
            }}>
            <div className="flex items-center gap-2 shrink-0">
              {done
                ? <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={2} style={{ color: active ? t.activeDot : t.doneCheck }} />
                : <span className="shrink-0" style={{
                    width: 5, height: 5, borderRadius: 999,
                    backgroundColor: active ? t.activeDot : t.idleDot,
                  }} />}
              <p className="tabular" style={{
                fontSize: active ? 21 : 15,
                fontWeight: 300,
                letterSpacing: active ? "-0.7px" : "-0.3px",
                lineHeight: 1.1,
                color: active ? t.activeLabel : done ? t.doneLabel : "var(--quiet)",
                transition: "font-size .4s var(--ease-brand)",
              }}>
                {r.label}
              </p>
            </div>
            <p className="order-last basis-full min-w-0 sm:basis-auto sm:mt-2"
              style={{
                fontSize: 13.5, fontWeight: 300, lineHeight: 1.35,
                color: active ? t.activeTitle : "var(--quiet)",
              }}>
              {r.title}
            </p>
            <p className="micro-label shrink-0 ml-auto text-right sm:ml-0 sm:text-left sm:mt-[7px]" style={{
              color: active ? t.activeStatus : done ? t.doneStatus : "var(--quiet)",
            }}>
              {r.status_text}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* ── Milestones and payments ──────────────────────────────────────────────── */

// One colour per payment state. Terracotta is the brand's only alert colour and
// it earns its place here: past due is the one line a client must not miss.
const PAYMENT_STATE = {
  paid: { color: "var(--estate)", dot: "var(--estate)" },
  partial: { color: "var(--estate)", dot: "var(--sage)" },
  invoiced: { color: "var(--ink)", dot: "var(--warm-label)" },
  overdue: { color: "var(--terracotta)", dot: "var(--terracotta)" },
  waived: { color: "var(--quiet)", dot: "var(--handle)" },
  upcoming: { color: "var(--quiet)", dot: "var(--handle)" },
};

function PaymentsBlock({ payments, onPay, canPay }) {
  const s = payments?.summary;
  if (!payments?.has_schedule || !s) return null;
  const milestones = payments.milestones || [];

  return (
    <>
      {/* Interest is stated separately from the balance, everywhere it appears.
          It is what the agreement entitles us to invoice — not money already on
          an invoice — and folding it into "remaining" would put a figure on this
          page that reconciles against nothing in the client's own records. */}
      {s.overdue_total > 0 && (
        <div style={{
          marginBottom: 26, padding: "16px 20px", borderRadius: R.well,
          backgroundColor: "#FAEDE9", border: "1px solid rgba(194,74,46,.22)",
        }}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
            <p style={{ fontSize: 14.5, fontWeight: 400, color: "var(--terracotta)" }}>
              {s.overdue_total_text} past due
              {s.overdue_count > 1 ? ` across ${s.overdue_count} milestones` : ""}
            </p>
            {s.late_fees_accrued_text && (
              <p className="micro-label" style={{ color: "var(--terracotta)" }}>
                + {s.late_fees_accrued_text} interest accrued
              </p>
            )}
          </div>
        </div>
      )}

      {/* The figures, stated the way a hotel statement would: totals first,
          hairline-ruled columns, the numeral leading its own caps label. */}
      <div className="cols-hairline grid sm:grid-cols-3" style={{ marginBottom: 30 }}>
        <div>
          <p className="tabular" style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-1px", color: "var(--ink)", lineHeight: 1.1 }}>
            {s.contract_total_text}
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Project total</p>
        </div>
        <div>
          <p className="tabular" style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-1px", color: "var(--estate)", lineHeight: 1.1 }}>
            {s.paid_total_text}
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Paid to date</p>
        </div>
        <div>
          <p className="tabular" style={{
            fontSize: 28, fontWeight: 300, letterSpacing: "-1px", lineHeight: 1.1,
            color: s.outstanding_total > 0 ? "var(--ink)" : "var(--quiet)",
          }}>
            {s.outstanding_total_text}
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Remaining</p>
        </div>
      </div>

      <div className="track" style={{ marginBottom: 30 }}>
        <div className="track-fill" style={{ width: `${Math.min(100, s.paid_percent || 0)}%` }} />
      </div>

      <div>
        {milestones.map((m, i) => {
          const tone = PAYMENT_STATE[m.state] || PAYMENT_STATE.upcoming;
          const isPaid = m.state === "paid";
          return (
            <div key={m.id} className="flex flex-wrap items-baseline gap-x-5 gap-y-2"
              style={{ paddingTop: i === 0 ? 0 : 18, paddingBottom: 18, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
              <span className="shrink-0 flex items-center" style={{ width: 16, alignSelf: "center" }}>
                {isPaid
                  ? <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--estate)" }} />
                  : <span className="progress-dot" style={{ backgroundColor: tone.dot, display: "block" }} />}
              </span>

              <div className="flex-1 min-w-0" style={{ minWidth: 180 }}>
                <p style={{ fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.1px", color: isPaid ? "var(--ink)" : "var(--body-dark)" }}>
                  {m.title}
                </p>
                <p className="micro-label" style={{ marginTop: 5, color: tone.color }}>
                  {[
                    m.status_text,
                    isPaid && m.paid_text ? m.paid_text : m.due_text ? `due ${m.due_text}` : null,
                    m.paid_amount_text ? `${m.paid_amount_text} received` : null,
                  ].filter(Boolean).join(" · ")}
                </p>

                {/* The projected delivery window your PM set. This is the line
                    that turns an invoice from a surprise into a reminder —
                    which is the entire argument this feature exists to end. */}
                {!isPaid && m.forecast && (
                  <p className="micro-label" style={{
                    marginTop: 6,
                    color: m.forecast.confidence === "confirmed" ? "var(--estate)" : "var(--tertiary)",
                  }}>
                    {m.forecast.confidence === "confirmed" ? "Delivery confirmed" : "Delivery projected"}
                    {" · "}{m.forecast.window_text}
                    {m.forecast.note ? ` · ${m.forecast.note}` : ""}
                  </p>
                )}

                {m.escalation?.late_fee_text && (
                  <p className="micro-label" style={{ marginTop: 6, color: "var(--terracotta)" }}>
                    {m.escalation.late_fee_text} interest accrued at 1.5% per month
                    {m.escalation.next_fee_text ? ` · next ${m.escalation.next_fee_text}` : ""}
                  </p>
                )}
              </div>

              <div className="shrink-0" style={{ textAlign: "right" }}>
                <p className="tabular" style={{
                  fontSize: 16, fontWeight: 300, letterSpacing: "-0.2px",
                  color: m.state === "waived" ? "var(--quiet)" : "var(--ink)",
                  textDecoration: m.state === "waived" ? "line-through" : "none",
                }}>
                  {m.amount_text}
                </p>
                {canPay && !isPaid && m.state !== "waived" && m.outstanding > 0 && (
                  <button onClick={() => onPay?.(m.id)} className="micro-label"
                    style={{
                      marginTop: 8, padding: "5px 14px", borderRadius: 999,
                      border: "1px solid var(--hairline-strong)", color: "var(--ink)",
                    }}>
                    Pay
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contract money that hasn't been assigned a milestone yet. Without this
          the figures above don't reconcile against the rows below. */}
      {s.unscheduled_total > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2"
          style={{ paddingTop: 18, paddingBottom: 18, borderTop: "1px solid var(--hairline)" }}>
          <span className="shrink-0" style={{ width: 16 }} />
          <div className="flex-1 min-w-0" style={{ minWidth: 180 }}>
            <p style={{ fontSize: 14.5, fontWeight: 300, letterSpacing: "-0.1px", color: "var(--quiet)" }}>
              Balance still to be scheduled
            </p>
            <p className="micro-label" style={{ marginTop: 5 }}>Not yet invoiced</p>
          </div>
          <p className="tabular shrink-0" style={{ fontSize: 16, fontWeight: 300, letterSpacing: "-0.2px", color: "var(--quiet)" }}>
            {s.unscheduled_total_text}
          </p>
        </div>
      )}

      <p className="body-text" style={{ marginTop: 26, maxWidth: "64ch", color: "var(--tertiary)" }}>
        Invoices are sent separately by email. If anything here doesn't match your records, tell your project
        lead and we'll reconcile it.
      </p>
    </>
  );
}

/* ── VEXOS subscription (billing) ─────────────────────────────────────────── */

// The subscription is a second agreement, not part of the project's milestone
// schedule — so it gets its own section rather than extra rows in the one above.
// Its months are the same visual language as the payment rows: dot, label,
// figure. Reuses PAYMENT_STATE so a paid month reads identically in both places.
function BillingBlock({ subscription }) {
  const s = subscription;
  if (!s?.has_subscription) return null;
  const periods = s.periods || [];
  const pending = s.pending_delivery;
  const issue = s.payment_issue;

  return (
    <>
      {/* A declined card, said plainly and above the figures. The client cannot
          act on a "Payment failed" chip buried in a table of twelve months, and
          the consequence — the platform pausing — is not something they should
          discover by it happening. */}
      {issue && (
        <div style={{
          marginBottom: 26, padding: "20px 24px", borderRadius: R.well,
          backgroundColor: issue.state === "suspended" ? "#FAEDE9" : "var(--warm-badge)",
          border: `1px solid ${issue.state === "suspended" ? "rgba(194,74,46,.26)" : "rgba(185,106,67,.28)"}`,
        }}>
          <p style={{
            fontSize: 16, fontWeight: 400, letterSpacing: "-0.3px",
            color: issue.state === "suspended" ? "var(--terracotta)" : "var(--warm-dark)",
          }}>
            {issue.headline}
          </p>
          <p className="body-text" style={{ marginTop: 8, maxWidth: "62ch" }}>{issue.detail}</p>
          {issue.fix_url && (
            <p style={{ marginTop: 14 }}>
              <a className="link-estate" href={issue.fix_url} target="_blank" rel="noreferrer">
                {issue.state === "suspended" ? "Settle and restore" : "Update payment"}
              </a>
            </p>
          )}
        </div>
      )}

      <div className="cols-hairline grid sm:grid-cols-3" style={{ marginBottom: 30 }}>
        <div>
          <p className="tabular" style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-1px", color: "var(--ink)", lineHeight: 1.1 }}>
            {s.monthly_amount_text}
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Per month</p>
        </div>
        <div>
          <p className="tabular" style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-1px", color: "var(--ink)", lineHeight: 1.1 }}>
            {s.term_months}
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Months</p>
        </div>
        <div>
          <p className="tabular" style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-1px", color: "var(--estate)", lineHeight: 1.1 }}>
            {s.paid_count}<span style={{ color: "var(--quiet)" }}>/{s.total_count}</span>
          </p>
          <p className="micro-label" style={{ marginTop: 9 }}>Settled</p>
        </div>
      </div>

      <div>
        {periods.map((p, i) => {
          const tone = PAYMENT_STATE[p.state] || PAYMENT_STATE.upcoming;
          const isPaid = p.state === "paid";
          return (
            <div key={p.period_number} className="flex flex-wrap items-baseline gap-x-5 gap-y-2"
              style={{ paddingTop: i === 0 ? 0 : 18, paddingBottom: 18, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
              <span className="shrink-0 flex items-center" style={{ width: 16, alignSelf: "center" }}>
                {isPaid
                  ? <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--estate)" }} />
                  : <span className="progress-dot" style={{ backgroundColor: tone.dot, display: "block" }} />}
              </span>

              <div className="flex-1 min-w-0" style={{ minWidth: 180 }}>
                <p style={{ fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.1px", color: isPaid ? "var(--ink)" : "var(--body-dark)" }}>
                  Month {p.period_number}
                </p>
                <p className="micro-label" style={{ marginTop: 5, color: tone.color }}>
                  {[
                    p.status_text,
                    isPaid && p.paid_text ? p.paid_text : p.due_text ? `due ${p.due_text}` : null,
                  ].filter(Boolean).join(" · ")}
                </p>
              </div>

              <p className="tabular shrink-0" style={{
                fontSize: 16, fontWeight: 300, letterSpacing: "-0.2px",
                color: p.state === "waived" ? "var(--quiet)" : "var(--ink)",
                textDecoration: p.state === "waived" ? "line-through" : "none",
              }}>
                {p.amount_text}
              </p>
            </div>
          );
        })}
      </div>

      <p className="body-text" style={{ marginTop: 26, maxWidth: "64ch", color: "var(--tertiary)" }}>
        {s.delivered_not_started
          ? `Your platform is live, so billing runs monthly from your platform launch date. We'll confirm your billing dates before the first payment.`
          : pending
            ? `Your platform fee is paid in monthly installments that begin on your VEXOS platform launch date. These months will be dated from that launch date.`
            : s.autopay
              ? "Your card on file is charged automatically at the start of each month. Receipts come from Stripe by email."
              : "Each month is invoiced by email. If anything here doesn't match your records, tell your project lead and we'll reconcile it."}
      </p>
    </>
  );
}

/* ── The calendar (right rail) ────────────────────────────────────────────── */

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EVENT_COLORS = {
  meeting: "var(--sage)",
  upcoming: "var(--estate)",
  milestone: "var(--ink)",
  eta: "var(--warm-label)",
  payment: "var(--terracotta)",
};
const EVENT_LEGEND = [
  { kind: "upcoming", label: "Booked" },
  { kind: "meeting", label: "Meetings" },
  { kind: "milestone", label: "Milestones" },
  { kind: "eta", label: "Delivery week" },
  { kind: "payment", label: "Payments" },
];

function pad2(n) { return String(n).padStart(2, "0"); }

function MiniCalendar({ events }) {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null); // date_key

  const byDay = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (!e.date_key) return;
      (map[e.date_key] = map[e.date_key] || []).push(e);
    });
    return map;
  }, [events]);

  const monthName = new Date(view.y, view.m, 1).toLocaleString("en-US", { month: "long" });
  const first = new Date(view.y, view.m, 1);
  const lead = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const todayKey = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;

  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => {
    setSelected(null);
    setView(({ y, m }) => {
      const next = new Date(y, m + delta, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });
  };

  const selectedEvents = selected ? byDay[selected] || [] : [];

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 16, fontWeight: 400, letterSpacing: "-0.3px", color: "var(--ink)" }}>
          {monthName} <span className="tabular" style={{ fontWeight: 300, color: "var(--tertiary)" }}>{view.y}</span>
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => move(-1)} aria-label="Previous month" className="p-1.5 rounded-full"
            style={{ border: "1px solid var(--hairline)" }}>
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--body)" }} />
          </button>
          <button onClick={() => move(1)} aria-label="Next month" className="p-1.5 rounded-full"
            style={{ border: "1px solid var(--hairline)" }}>
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--body)" }} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7" style={{ rowGap: 2 }}>
        {DOW.map((d) => (
          <p key={d} className="micro-label text-center" style={{ fontSize: 9.5, marginBottom: 6 }}>{d}</p>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={`x${i}`} />;
          const key = `${view.y}-${pad2(view.m + 1)}-${pad2(d)}`;
          const dayEvents = byDay[key] || [];
          const isToday = key === todayKey;
          const isSelected = key === selected;
          return (
            <button key={key} onClick={() => setSelected(isSelected ? null : key)}
              className="flex flex-col items-center"
              style={{ paddingTop: 4, paddingBottom: 4, borderRadius: R.chip, backgroundColor: isSelected ? "var(--image-well)" : "transparent" }}>
              <span className="tabular inline-flex items-center justify-center rounded-full"
                style={{
                  width: 26, height: 26, fontSize: 12.5,
                  fontWeight: isToday ? 400 : 300,
                  color: isToday ? "#fff" : dayEvents.length ? "var(--ink)" : "var(--quiet)",
                  backgroundColor: isToday ? "var(--ink)" : "transparent",
                }}>
                {d}
              </span>
              <span className="flex gap-0.5" style={{ height: 4, marginTop: 2 }}>
                {dayEvents.slice(0, 3).map((e, j) => (
                  <span key={j} style={{
                    width: 3.5, height: 3.5, borderRadius: 999,
                    backgroundColor: EVENT_COLORS[e.kind] || "var(--handle)",
                  }} />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      {selected && selectedEvents.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--hairline)" }}>
          {selectedEvents.map((e, i) => (
            <div key={i} className="flex items-start gap-2.5" style={{ marginTop: i === 0 ? 0 : 8 }}>
              <span style={{
                width: 4, height: 4, borderRadius: 999, marginTop: 7,
                backgroundColor: EVENT_COLORS[e.kind] || "var(--handle)",
              }} />
              <p style={{ fontSize: 13, fontWeight: 300, color: "var(--body-dark)", lineHeight: 1.5 }}>{e.label}</p>
            </div>
          ))}
        </div>
      )}
      {selected && selectedEvents.length === 0 && (
        <p className="micro-label" style={{ marginTop: 14 }}>Nothing on this day</p>
      )}

      {/* Only legend what this project actually has — a "Payments" key on a
          tracker with money hidden would advertise a schedule we're not showing. */}
      <div className="flex flex-wrap" style={{ marginTop: 16, gap: "6px 14px" }}>
        {EVENT_LEGEND.filter((l) => (events || []).some((e) => e.kind === l.kind)).map((l) => (
          <span key={l.kind} className="inline-flex items-center gap-1.5">
            <span style={{ width: 4, height: 4, borderRadius: 999, backgroundColor: EVENT_COLORS[l.kind] }} />
            <span className="micro-label" style={{ fontSize: 9.5 }}>{l.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Next meeting (right rail) ────────────────────────────────────────────── */

function NextMeetingCard({ meeting }) {
  if (!meeting) {
    return (
      <div style={{ padding: "20px 22px", borderRadius: R.panel, border: "1px solid var(--hairline)", backgroundColor: "var(--surface)" }}>
        <p className="micro-label">Next meeting</p>
        <p className="body-text" style={{ marginTop: 10 }}>
          Nothing on the calendar. Your project lead will add anything you book together.
        </p>
      </div>
    );
  }
  return (
    <div className="ink-section" style={{ padding: "22px 24px", borderRadius: R.panel }}>
      <div className="flex items-center justify-between gap-3">
        <p className="micro-label">{meeting.is_today ? "Today" : "Next meeting"}</p>
        {meeting.is_today && <span className="live-dot" />}
      </div>
      <p style={{ marginTop: 12, fontSize: 19, fontWeight: 300, letterSpacing: "-0.4px", color: "#fff", lineHeight: 1.25 }}>
        {meeting.title}
      </p>
      <p className="tabular" style={{ marginTop: 8, fontSize: 13.5, fontWeight: 300, color: "rgba(255,255,255,.62)" }}>
        {meeting.date_text} · {meeting.time_text}
        {meeting.duration_text ? ` · ${meeting.duration_text}` : ""}
      </p>
      {meeting.note && (
        <p style={{ marginTop: 10, fontSize: 13.5, fontWeight: 300, color: "rgba(255,255,255,.62)", lineHeight: 1.55 }}>
          {meeting.note}
        </p>
      )}
      {meeting.join_url && (
        <a href={meeting.join_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2"
          style={{
            marginTop: 16, padding: "9px 22px", borderRadius: 999,
            backgroundColor: "#FFFFFF", color: "#000000",
            fontSize: 13.5, fontWeight: 400, letterSpacing: "-0.25px",
          }}>
          Join <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>
      )}
    </div>
  );
}

/* ── Notifications ────────────────────────────────────────────────────────── */

function buildNotifications(t) {
  const ev = [];
  (t.updates || []).forEach((u) => ev.push({
    at: u.created_at, text: u.body, meta: u.author_name ? `Update · ${u.author_name}` : "Update",
  }));
  (t.rounds || []).forEach((r) => {
    if (r.delivered_at) ev.push({ at: r.delivered_at, text: `${r.label} · ${r.title} is with you for review`, meta: "Delivery" });
    if (r.approved_at) ev.push({ at: r.approved_at, text: `${r.label} approved`, meta: "Delivery" });
  });
  (t.phases || []).forEach((p) => {
    if (p.entered_at && p.key !== "queued") ev.push({ at: p.entered_at, text: `Your project entered ${p.label}`, meta: "Phase" });
  });
  if (t.timeline_started_at) ev.push({ at: t.timeline_started_at, text: "Your project officially started", meta: "Milestone" });
  /* Payments ride the same bell as everything else, so "when is my next
     payment" is answerable without hunting for the schedule. Dated by DUE date
     rather than by now, so they sort into the timeline where they belong. */
  (t.payments?.milestones || []).forEach((m) => {
    if (!m.due_date || m.state === "paid" || m.state === "waived") return;
    const late = m.escalation?.is_overdue;
    ev.push({
      at: m.due_date,
      text: late
        ? `${m.title} · ${m.amount_text} is ${m.escalation.days_overdue} days past due`
        : `${m.title} · ${m.amount_text} due ${m.due_text}`,
      meta: late ? "Payment overdue" : "Payment",
    });
  });
  (t.meetings || []).slice(0, 5).forEach((m) => {
    if (m.at) ev.push({ at: m.at, text: m.title, meta: "Meeting" });
  });
  return ev
    .filter((e) => e.at)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, 15);
}

function NotificationsBell({ tracker, token }) {
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => {
    try { return localStorage.getItem(seenKey(token)) || ""; } catch { return ""; }
  });
  const ref = useRef(null);

  const items = useMemo(() => buildNotifications(tracker), [tracker]);
  const unseen = items.filter((e) => String(e.at) > seenAt).length;

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && items.length) {
      const latest = String(items[0].at);
      setSeenAt(latest);
      try { localStorage.setItem(seenKey(token), latest); } catch { /* private mode */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} aria-label="Notifications"
        className="relative inline-flex items-center justify-center rounded-full"
        style={{ width: 40, height: 40, border: "1px solid var(--hairline)", backgroundColor: "var(--surface)" }}>
        <Bell className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--body)" }} />
        {unseen > 0 && (
          <span className="absolute" style={{
            top: 9, right: 10, width: 6, height: 6, borderRadius: 999,
            backgroundColor: "var(--estate)",
          }} />
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50" style={{
          top: 48, width: 330, maxHeight: 420, overflowY: "auto",
          backgroundColor: "var(--surface)", border: "1px solid var(--hairline-strong)", borderRadius: R.panel,
        }}>
          <p className="micro-label" style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--hairline)" }}>
            Notifications
          </p>
          {items.length === 0 && (
            <p className="body-text" style={{ padding: "16px 20px 20px" }}>Nothing yet — activity on your project will appear here.</p>
          )}
          {items.map((e, i) => (
            <div key={i} style={{ padding: "14px 20px", borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
              <p style={{ fontSize: 13.5, fontWeight: 300, color: "var(--body-dark)", lineHeight: 1.5 }}>{e.text}</p>
              <p className="micro-label" style={{ marginTop: 6 }}>{e.meta}{fmtShort(e.at) ? ` · ${fmtShort(e.at)}` : ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtShort(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return null; }
}

/* ── Sidebar ──────────────────────────────────────────────────────────────── */

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { key: "todo", label: "Client to do", icon: ListChecks },
  // Sits directly under the to-do list because that is where it is used from:
  // a client hands an item to their architect, and this is where the architect
  // got added. Filing it under Settings made "who is on this project" an
  // administrative question rather than the first one a client asks.
  { key: "team", label: "Your team", icon: Users },
  { key: "meetings", label: "Meetings", icon: Video },
  { key: "documents", label: "Documents", icon: FolderOpen },
];

/* The rail is FIXED to the window, not sticky inside the centred layout.
   Sticky meant the rail started wherever the 1560px column started — on a wide
   screen that left a band of bare canvas to its left, with the rail's own
   surface and hairline drawing attention to it. Anchored to the viewport edge
   there is nothing to its left to be empty.

   It carries more than navigation now: which project this is (the only place
   that says so on the inner pages) and the four numbers, so the middle of the
   rail is doing work instead of being the emptiest part of the page. */
const RAIL_WIDTH = 232;

function Sidebar({ page, setPage, tracker }) {
  const stats = projectStats(tracker);
  // The rail names ONE person, and the contacts payload is already in the order
  // a client meets them, so the lead is simply the first of them. Falls back to
  // the legacy `pm_*` fields so a tracker fetched before this shipped — or one
  // whose project has no assignment at all — still shows whoever it knows.
  const lead = (tracker.contacts || [])[0] || (tracker.pm_name ? {
    name: tracker.pm_name, role: tracker.pm_role_title || "Your project lead",
    email: tracker.pm_email, initials: tracker.pm_initials,
  } : null);
  const others = Math.max(0, (tracker.contacts || []).length - 1)
    + (tracker.team_members || []).length;
  return (
    <aside className="hidden lg:flex flex-col overflow-y-auto"
      /* Positioned INLINE, not with `fixed top-0 …`: index.css pins every direct
         child of `.paper-wash` to `position: relative` so it clears the wash
         behind it, and that rule beats the utility class. An inline style is the
         one thing it cannot override. */
      style={{
        position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 30, width: RAIL_WIDTH,
        backgroundColor: "var(--surface)", borderRight: "1px solid var(--hairline)",
      }}>
      <div style={{ padding: "26px 28px 18px" }}>
        {/* The official mark is white-on-BLACK (drawn for HQ's ink sidebar, no
            alpha); invert() sets it in ink and multiply melts the inverted
            white plate into whatever light surface sits behind it. */}
        <img src="/trp-logo.png" alt="The Render Pros"
          style={{ height: 26, objectFit: "contain", filter: "invert(1)", mixBlendMode: "multiply" }} />
        {/* Whose project, and which one. The main column only names it in the
            dashboard's welcome line, so on Meetings or Documents this is the
            answer to "am I looking at the right project". */}
        <p style={{
          marginTop: 16, fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.2px",
          color: "var(--ink)", lineHeight: 1.3,
        }}>
          {tracker.program_name}
        </p>
        {tracker.client_name && (
          <p className="micro-label" style={{ marginTop: 6 }}>{tracker.client_name}</p>
        )}
      </div>

      <nav style={{ padding: "6px 0 18px" }}>
        <p className="micro-label" style={{ padding: "0 28px", marginBottom: 12 }}>Your project</p>
        {NAV.map((item) => <NavItem key={item.key} item={item} page={page} setPage={setPage} />)}

        <p className="micro-label" style={{ padding: "0 28px", marginTop: 24, marginBottom: 12 }}>Account</p>
        <NavItem item={{ key: "settings", label: "Profile & settings", icon: Settings2 }} page={page} setPage={setPage} />
      </nav>

      {/* The one-line version of the dashboard's hero: which phase, how far in.
          The dashboard says this at length; the other four pages said it
          nowhere, and this is the question a client opens the page with. */}
      {tracker.phase_label && (
        <div style={{ padding: "20px 28px", borderTop: "1px solid var(--hairline)" }}>
          <p className="micro-label">{tracker.on_hold ? "Paused at" : "Current phase"}</p>
          <p style={{
            marginTop: 8, fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.2px",
            color: "var(--ink)", lineHeight: 1.3,
          }}>
            {tracker.on_hold ? (tracker.paused_at_phase || tracker.phase_label) : tracker.phase_label}
          </p>
          {/* A hairline track rather than a ring: the rail is 232px wide and the
              dashboard already owns the big version of this number. */}
          <div style={{
            marginTop: 12, height: 3, borderRadius: 999, backgroundColor: "var(--hairline)", overflow: "hidden",
          }}>
            <div style={{
              width: `${Math.max(0, Math.min(100, tracker.progress_percent || 0))}%`,
              height: "100%", borderRadius: 999,
              backgroundColor: tracker.on_hold ? "var(--quiet)" : "var(--estate)",
              transition: "width .6s var(--ease-brand)",
            }} />
          </div>
          <p className="micro-label" style={{ marginTop: 8 }}>
            {Math.round(tracker.progress_percent || 0)}% complete
          </p>
        </div>
      )}

      {/* Where things stand, on every page rather than only the dashboard. Rows
          rather than tiles: a 232px rail has room for the number and the word,
          and four tiles here would be a second dashboard in the margin. */}
      <div style={{ padding: "20px 28px 22px", borderTop: "1px solid var(--hairline)" }}>
        <p className="micro-label" style={{ marginBottom: 14 }}>Where things stand</p>
        {stats.map((s, i) => (
          <div key={s.key} className="flex items-baseline justify-between gap-3"
            style={{
              paddingTop: i === 0 ? 0 : 9.5, paddingBottom: i === stats.length - 1 ? 0 : 9.5,
              borderBottom: i === stats.length - 1 ? "none" : "1px solid var(--hairline)",
            }}>
            <span className="min-w-0" style={{ fontSize: 13, fontWeight: 300, color: "var(--body)", letterSpacing: "-0.1px" }}>
              {s.label}
            </span>
            <span className="tabular shrink-0" style={{ fontSize: 14, fontWeight: 400, color: "var(--ink)" }}>
              {s.value}
            </span>
          </div>
        ))}
      </div>

      {/* Anything left over falls HERE, between the two blocks, so the rail ends
          on the project lead rather than on a lone line of small print. */}
      <div className="flex-1" style={{ minHeight: 24 }} />

      {/* The named human — the white-glove touch, present on every page, and
          now with a face on it. `contacts[0]` is the project lead; the rest of
          the team is one click away rather than crammed into a 232px rail. */}
      {lead && (
        <div style={{ padding: "22px 28px", borderTop: "1px solid var(--hairline)" }}>
          <div className="flex items-center gap-3">
            <Avatar person={lead} size={38} />
            <div className="min-w-0">
              <p style={{ fontSize: 13.5, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.1px" }}>{lead.name}</p>
              <p className="micro-label" style={{ marginTop: 3 }}>{lead.role}</p>
            </div>
          </div>
          {lead.email && (
            <a className="link-estate block truncate" style={{ marginTop: 12, fontSize: 13 }}
              href={`mailto:${lead.email}`}>{lead.email}</a>
          )}
          {others > 0 && (
            <button className="link-estate block" style={{ marginTop: 10, fontSize: 12.5 }}
              onClick={() => setPage("team")}>
              {others === 1 ? "One more person on your team" : `${others} more people on your team`}
            </button>
          )}
        </div>
      )}
      <p className="micro-label" style={{ padding: "16px 28px 22px", fontSize: 9.5, lineHeight: 1.6 }}>
        This link is yours for the life of the project
      </p>
    </aside>
  );
}

function NavItem({ item, page, setPage }) {
  const active = page === item.key;
  const Icon = item.icon;
  // The selected item is a soft pill inset into the gutter, not a full-bleed
  // band with a hard rule down its edge. Margin + padding still add up to the
  // 28px gutter, so the icons stay on the same line as the section labels.
  return (
    <button onClick={() => setPage(item.key)}
      className="flex items-center gap-3 text-left"
      style={{
        width: "calc(100% - 28px)",
        margin: "0 14px",
        padding: "11px 14px",
        borderRadius: 999,
        backgroundColor: active ? "var(--selected-tint)" : "transparent",
        transition: "background-color .25s var(--ease-brand)",
      }}>
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5}
        style={{ color: active ? "var(--estate)" : "var(--tertiary)" }} />
      <span style={{
        fontSize: 14, fontWeight: active ? 400 : 300, letterSpacing: "-0.1px",
        color: active ? "var(--estate)" : "var(--body)",
      }}>
        {item.label}
      </span>
    </button>
  );
}

// Mobile: the sidebar collapses into a horizontal tab row under the header.
function MobileNav({ page, setPage }) {
  const items = [...NAV, { key: "settings", label: "Settings", icon: Settings2 }];
  return (
    <div className="lg:hidden flex overflow-x-auto" style={{ borderBottom: "1px solid var(--hairline)", gap: 4 }}>
      {items.map((item) => {
        const active = page === item.key;
        return (
          <button key={item.key} onClick={() => setPage(item.key)}
            className="shrink-0"
            style={{
              padding: "12px 14px",
              fontSize: 14, fontWeight: active ? 400 : 300, letterSpacing: "-0.1px",
              color: active ? "var(--estate)" : "var(--body)",
              borderBottom: `1px solid ${active ? "var(--estate)" : "transparent"}`,
              marginBottom: -1,
            }}>
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Checklist item (the client's task) ───────────────────────────────────── */

/* ── One line of the client's to-do list ────────────────────────────────────
 *
 * Files arrive as LINKS, not uploads. Clients were already emailing a Dropbox
 * folder rather than pushing forty CAD files through a browser form, and the
 * upload path they were offered instead wrote to storage nobody in the studio
 * could read back. So the ask is now the thing they actually do.
 *
 * `upload` / `multi_upload` items still exist on older projects. They render
 * read-only: what was sent stays visible, and there is no way to add more.
 */
/* Handing one item to somebody on the client's own side.
 *
 * Deliberately not a permission — everyone holding the link can already see
 * and edit everything, and a second access model on a page whose whole premise
 * is one link would be a lie about what happens. What this does is name an
 * owner, email them the item, and give the client a list of who they are
 * waiting on. The row still works exactly as before for whoever opens it.
 *
 * The control is quiet until it is used: an unassigned item shows one small
 * link, not a dropdown per row. Forty-four rows each carrying an open picker is
 * the page this list spent a year getting away from.
 */
function AssignControl({ item, members, busy, onAssign, onOpenTeam }) {
  const [open, setOpen] = useState(false);
  const assignee = item.assignee;

  if (assignee) {
    return (
      <div className="inline-flex items-center gap-2" style={{ marginTop: 10 }}>
        <Avatar person={assignee} size={22}
          style={{ backgroundColor: "var(--image-well)", color: "var(--body)" }} />
        <span style={{ fontSize: 12.5, fontWeight: 300, color: "var(--tertiary)" }}>
          Asked {assignee.name}
          {assignee.role_label ? ` · ${assignee.role_label}` : ""}
        </span>
        <button onClick={() => onAssign(item, null)} disabled={busy}
          aria-label={`Stop asking ${assignee.name} for this`}>
          {busy
            ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />
            : <X className="w-3 h-3" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button className="inline-flex items-center gap-1.5" style={{ marginTop: 10 }}
        onClick={() => setOpen(true)}>
        <UserPlus className="w-3 h-3" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />
        <span style={{ fontSize: 12.5, fontWeight: 300, color: "var(--tertiary)" }}>
          Ask someone else for this
        </span>
      </button>
    );
  }

  // Nobody to hand it to yet. Said as the next step rather than as an empty
  // menu — an empty picker is a dead end a client has to work out for themselves.
  if (!members.length) {
    return (
      <p style={{ marginTop: 10, fontSize: 12.5, fontWeight: 300, color: "var(--tertiary)" }}>
        Add your architect, designer or developer on{" "}
        <button className="link-estate" style={{ fontSize: "inherit" }} onClick={onOpenTeam}>
          Your team
        </button>{" "}
        and you can pass this straight to them.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
      {members.map((m) => (
        <button key={m.id} onClick={() => { onAssign(item, m.id); setOpen(false); }}
          disabled={busy}
          className="inline-flex items-center gap-2"
          style={{
            padding: "5px 12px 5px 5px", borderRadius: 999,
            backgroundColor: "var(--image-well)", border: "1px solid var(--hairline)",
            fontSize: 12.5, fontWeight: 300, color: "var(--body-dark)",
          }}>
          <Avatar person={m} size={20}
            style={{ backgroundColor: "var(--surface)", color: "var(--body)" }} />
          {m.name}
        </button>
      ))}
      <button className="link-estate" style={{ fontSize: 12.5 }} onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  );
}

function ChecklistItem({ item, first, busy, members, onAddLink, onRemoveLink, onSave,
                        onAssign, onOpenTeam }) {
  const isLink = item.type === "link";
  const isLegacyFile = item.type === "upload" || item.type === "multi_upload";
  const done = item.status === "complete" || item.status === "n_a";
  const value = item.value_json?.value;
  const links = item.value_json?.links || [];
  const files = item.value_json?.files || [];

  return (
    <div style={{
      paddingTop: first ? 0 : 22, paddingBottom: 22,
      borderTop: first ? "none" : "1px solid var(--hairline)",
    }}>
      <div className="flex items-start gap-3">
        <span style={{ paddingTop: 5 }}>
          {done
            ? <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--estate)" }} />
            : <span className="progress-dot" style={{ backgroundColor: "var(--handle)", display: "block", marginTop: 4 }} />}
        </span>
        <div className="flex-1 min-w-0">
          <p style={{ fontSize: 14, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.1px" }}>
            {item.label}
            {item.required && !done && <span style={{ color: "var(--terracotta)", marginLeft: 6 }}>required</span>}
          </p>
          {item.instructions && (
            <p className="body-text" style={{ marginTop: 5, color: "var(--tertiary)" }}>{item.instructions}</p>
          )}

          {/* Who is doing this one. Under the instructions, because it answers
              "this isn't mine" — which is the thought the instructions provoke. */}
          {!done && (
            <AssignControl item={item} members={members} busy={busy}
              onAssign={onAssign} onOpenTeam={onOpenTeam} />
          )}

          {/* Said plainly, because otherwise a client sees a tick they did not
              put there and assumes the page is wrong. */}
          {item.status === "n_a" && (
            <p className="body-text" style={{ marginTop: 6, color: "var(--tertiary)" }}>
              Marked not applicable to this project{item.na_reason ? ` — ${item.na_reason}` : ""}.
            </p>
          )}
          {item.recorded_for_you && item.status !== "n_a" && (
            <p className="body-text" style={{ marginTop: 6, color: "var(--tertiary)" }}>
              Your project lead recorded this for you.
            </p>
          )}

          <div style={{ marginTop: 14 }}>
            {isLink && (
              <LinkAnswer
                links={links}
                busy={busy}
                onAdd={(payload) => onAddLink(item, payload)}
                onRemove={(linkId) => onRemoveLink(item, linkId)}
              />
            )}

            {/* Files sent before the switch to links. Shown for any item that
                has them, whatever its type now says — a client's own work must
                not vanish from their page because we changed how we ask. */}
            {files.length > 0 && (
              <div style={{ marginBottom: isLink ? 12 : 0 }}>
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3"
                    style={{ padding: "10px 14px", backgroundColor: "var(--image-well)", borderRadius: R.well, marginBottom: 6 }}>
                    <FileText className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} style={{ color: "var(--tertiary)" }} />
                    <span className="flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 300, color: "var(--body-dark)" }}>{f.name}</span>
                    <span className="shrink-0" style={{ fontSize: 12, fontWeight: 300, color: "var(--quiet)" }}>received</span>
                  </div>
                ))}
              </div>
            )}

            {isLegacyFile && files.length === 0 && (
              <p className="body-text" style={{ color: "var(--tertiary)" }}>
                Send this one to your project lead and they'll log it here.
              </p>
            )}

            {item.type === "input" && (
              <input className="w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate" defaultValue={value || ""} placeholder="Type your answer"
                onBlur={(e) => { const v = e.target.value.trim(); onSave(item, v); }} />
            )}

            {item.type === "textarea" && (
              <textarea className="w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate" rows={3} defaultValue={value || ""} placeholder="Type your answer"
                style={{ resize: "vertical" }}
                onBlur={(e) => { const v = e.target.value.trim(); onSave(item, v); }} />
            )}

            {item.type === "toggle" && (
              <label className="inline-flex items-center gap-2.5" style={{ cursor: "pointer", fontSize: 14, fontWeight: 300, color: "var(--body-dark)" }}>
                <input type="checkbox" defaultChecked={value === true}
                  onChange={(e) => onSave(item, e.target.checked)}
                  style={{ accentColor: "var(--estate)", width: 15, height: 15 }} />
                Yes
              </label>
            )}

            {item.type === "select" && (
              <select className="w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate" defaultValue={value || ""}
                onChange={(e) => onSave(item, e.target.value)}>
                <option value="">Select</option>
                {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>
        </div>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" strokeWidth={1.5} style={{ color: "var(--quiet)", marginTop: 5 }} />}
      </div>
    </div>
  );
}

/* The link list on one item, plus the form to add to it.
 *
 * The form only appears once the client asks for it, so an item that already
 * has its link reads as done rather than as another empty box to fill. The
 * optional label earns its place on items where two links are normal — "CAD
 * package" and "reference photos" against the same row. */
function LinkAnswer({ links, busy, onAdd, onRemove }) {
  const [open, setOpen] = useState(links.length === 0);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  const submit = async () => {
    if (!url.trim()) return;
    const ok = await onAdd({ url: url.trim(), label: label.trim(), note: note.trim() });
    if (ok) { setUrl(""); setLabel(""); setNote(""); setOpen(false); }
  };

  const field = "w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate";

  return (
    <div>
      {links.map((l) => (
        <div key={l.id} className="flex items-start gap-3"
          style={{ padding: "11px 14px", backgroundColor: "var(--image-well)", borderRadius: R.well, marginBottom: 6 }}>
          <LinkIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} style={{ color: "var(--tertiary)", marginTop: 3 }} />
          <div className="flex-1 min-w-0">
            {l.label && (
              <p style={{ fontSize: 13.5, fontWeight: 400, color: "var(--body-dark)" }}>{l.label}</p>
            )}
            <a className="link-estate block truncate" href={l.url} target="_blank" rel="noreferrer"
              style={{ fontSize: 13.5, fontWeight: 300 }}>
              {l.url}
            </a>
            {l.note && (
              <p style={{ fontSize: 12.5, fontWeight: 300, color: "var(--tertiary)", marginTop: 2 }}>{l.note}</p>
            )}
          </div>
          <button onClick={() => onRemove(l.id)} aria-label="Remove this link" disabled={busy}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />
          </button>
        </div>
      ))}

      {open ? (
        <div style={{ marginTop: links.length ? 10 : 0, display: "flex", flexDirection: "column", gap: 8 }}>
          <input className={field} value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste the link — Dropbox, Drive, WeTransfer…"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
          <div className="flex flex-col sm:flex-row gap-2">
            <input className={field} value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="What's in it? (optional)" />
            <input className={field} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Password or note (optional)" />
          </div>
          <div className="flex items-center gap-3">
            <button className="btn-primary" style={{ padding: "9px 22px", fontSize: 13.5 }}
              onClick={submit} disabled={busy || !url.trim()}>
              Save link
            </button>
            {links.length > 0 && (
              <button className="link-estate" style={{ fontSize: 13 }} onClick={() => setOpen(false)}>
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        /* inline-flex, or .btn-secondary lets the plus sign and the words land
           on two lines and the pill comes out looking broken. */
        <button className="btn-secondary inline-flex items-center gap-2 whitespace-nowrap"
          style={{ padding: "9px 20px", fontSize: 13.5, marginTop: 4 }}
          onClick={() => setOpen(true)}>
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          Add another link
        </button>
      )}
    </div>
  );
}

/* ── Dashboard page ───────────────────────────────────────────────────────── */

function DashboardPage({ tracker, openTodo, onPay, highlight }) {
  // Which milestone terms this client was sold. The rails themselves arrive
  // already worded by the server; this only governs the section copy around
  // them, so a project from before the V1 Clay rename never reads as though it
  // bought a delivery it didn't. Absent means an older project — see
  // `_milestone_policy` in routers/project_tracker.py.
  const isClayMilestones = tracker.milestone_policy === "clay";
  const files = tracker.files || {};
  const started = tracker.timeline_started;
  const running = tracker.clock_running;
  const preStart = !running && tracker.phase_key === "queued";
  const onHold = tracker.on_hold;
  const needsFiles = !files.files_complete && files.has_checklist;
  const payments = tracker.payments;

  // Ours first, then theirs — the same order as the team page, so the faces in
  // this cluster are in the order a client will find them when they click it.
  const people = [...(tracker.contacts || []), ...(tracker.team_members || [])];

  return (
    <div>
      {/* The state of play, stated plainly, before anything decorative. */}
      {/* Suppressed when the pause is a BILLING hold: the banner above already
          says the project is paused and says why, and "your project manager can
          tell you what's needed" is actively wrong there — the PM isn't the
          answer, the invoice is. Two pause messages that disagree is worse than
          one that's blunt. */}
      {onHold && !tracker.billing_hold && (
        <Reveal index={0}>
          <Notice tone="warm" title="Your project is on hold">
            Work is paused{tracker.paused_at_phase ? <> during <em style={{ fontStyle: "normal", color: "var(--warm-dark)" }}>{tracker.paused_at_phase}</em></> : null}.
            Your project manager can tell you exactly what's needed to restart it.
          </Notice>
        </Reveal>
      )}

      {!onHold && preStart && (
        <Reveal index={0}>
          <Notice tone="warm" title="Your timeline hasn't started yet">
            Your project clock starts the moment we've received everything we need. That's how we're able to
            commit to a delivery window and hold ourselves to it.
            {files.required_total > 0 && <> You've provided {files.required_complete} of {files.required_total} required items.</>}
          </Notice>
        </Reveal>
      )}

      {!onHold && preStart && files.files_complete && (
        <Reveal index={0}>
          <Notice tone="green" title="Everything we need is in">
            Thank you. Your project manager is doing a final check on your files and will start your project
            shortly. You'll get an email the moment the clock starts.
          </Notice>
        </Reveal>
      )}

      {/* Where each production stands, before anything else. VEXOS and
          visualization run in parallel and are different productions, so they
          get a rail each rather than one merged line. */}
      <Reveal index={0}>
        <div style={{ marginTop: 32 }}>
          <p className="micro-label" style={{ marginBottom: 14 }}>Your delivery</p>

          <p style={{ fontSize: 15, fontWeight: 400, letterSpacing: "-0.2px", color: "var(--ink)", marginBottom: 10 }}>
            Visualization
          </p>
          <DeliveryRail rounds={tracker.rounds} />

          {tracker.has_vexos && (tracker.vexos_rounds || []).length > 0 && (
            <>
              {/* The platform's own rail, on its own axis. The rule above it is
                  the full-strength hairline rather than the ordinary one, and
                  the name is set as a mark — this is a second production, not
                  a continuation of the one above. */}
              <div className="flex items-baseline gap-3" style={{
                marginTop: 28, marginBottom: 12, paddingTop: 24,
                borderTop: "1px solid var(--hairline-strong)",
              }}>
                <span className="vexos-mark">VEXOS</span>
                <span className="micro-label">Platform</span>
              </div>
              <DeliveryRail rounds={tracker.vexos_rounds} variant="vexos" />
              {/* What the milestone you're on actually covers. This rail is
                  the VEXOS build itself — a different production from the V1
                  Clay visualization milestone that gates Payment 2 — and that
                  boundary is the one clients most often get wrong, so it's
                  stated on the page, not just in the proposal. */}
              {(() => {
                const r = tracker.vexos_rounds[currentRoundIndex(tracker.vexos_rounds)];
                if (!r?.blurb) return null;
                /* A ruled note rather than a warm well: the wells on this page
                   are the visualization side's, and a second filled box under
                   the ink rail would undo the separation the rail just made. */
                return (
                  <div style={{ marginTop: 20, paddingLeft: 18, borderLeft: "1px solid var(--hairline-strong)" }}>
                    <p className="micro-label" style={{ marginBottom: 9, color: "var(--ink)" }}>
                      What “{r.label}” means
                    </p>
                    <p className="body-text" style={{ maxWidth: "72ch" }}>{r.blurb}</p>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </Reveal>

      {/* The two hero cards. Ink for where you are; the ring for how far along. */}
      <Reveal index={1}>
        <div className="grid md:grid-cols-2" style={{ gap: 20, marginTop: 32 }}>
          <div className="ink-section flex flex-col" style={{ padding: "26px 28px", borderRadius: R.panel }}>
            <div className="flex items-center justify-between gap-3">
              <p className="micro-label">Happening now</p>
              <span className={cn("inline-flex items-center gap-1.5 micro-label rounded-full px-3 py-1.5",
                !onHold && running ? "bg-estate-50 !text-estate-600" : "bg-white/10 !text-white/80")}>
                {!onHold && running && <span className="live-dot" />}
                {tracker.phase_label}
              </span>
            </div>
            <p style={{ marginTop: 18, fontSize: "clamp(21px, 2.2vw, 26px)", fontWeight: 300, letterSpacing: "-0.7px", color: "#fff", lineHeight: 1.2 }}>
              {tracker.phase_headline}
            </p>
            {tracker.next_up_label && (
              <p style={{ marginTop: 14, fontSize: 14, fontWeight: 300, color: "rgba(255,255,255,.62)", lineHeight: 1.6 }}>
                <span style={{ color: "var(--sage)" }}>Next · </span>
                {tracker.next_up_label}{tracker.next_up_detail ? ` — ${tracker.next_up_detail}` : ""}
              </p>
            )}
            <div className="flex items-end justify-between gap-4" style={{ marginTop: "auto", paddingTop: 22 }}>
              <MonogramRow people={people} />
              {needsFiles && !onHold && (
                <button onClick={() => openTodo(null)}
                  className="inline-flex items-center gap-2 shrink-0"
                  style={{
                    padding: "9px 22px", borderRadius: 999, backgroundColor: "#FFFFFF", color: "#000000",
                    fontSize: 13.5, fontWeight: 400, letterSpacing: "-0.25px",
                  }}>
                  {files.outstanding_required ?? 0} item{(files.outstanding_required ?? 0) === 1 ? "" : "s"} needed
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{
            padding: "24px 26px", borderRadius: R.panel, gap: 24,
            backgroundColor: "var(--surface)", border: "1px solid var(--hairline)",
          }}>
            <ProgressRing percent={tracker.progress_percent || 0} size={136} />
            <div className="min-w-0 flex-1">
              {(tracker.rounds || []).map((r, i) => {
                const approved = r.status === "approved";
                const delivered = r.status === "delivered";
                return (
                  <div key={r.key}
                    style={{ paddingTop: i === 0 ? 0 : 11, paddingBottom: 11, borderBottom: i === tracker.rounds.length - 1 ? "none" : "1px solid var(--hairline)" }}>
                    <div className="flex items-center gap-2.5">
                      <span className="progress-dot shrink-0" style={{
                        backgroundColor: approved ? "var(--estate)" : delivered ? "var(--sage)" : "var(--handle)",
                        display: "block",
                      }} />
                      <span className="flex-1 truncate" style={{ fontSize: 13, fontWeight: 400, letterSpacing: "-0.1px", color: approved || delivered ? "var(--ink)" : "var(--quiet)" }}>
                        {r.label} · {r.title}
                      </span>
                    </div>
                    <p className="micro-label" style={{ marginTop: 4, paddingLeft: 16 }}>{r.status_text}</p>
                  </div>
                );
              })}
              {started && tracker.eta_weeks_text && (
                <p className="micro-label" style={{ marginTop: 14 }}>
                  Est. delivery {tracker.eta_weeks_text}{tracker.eta_target_text ? ` · ${tracker.eta_target_text}` : ""}
                </p>
              )}
            </div>
          </div>
        </div>
      </Reveal>

      {tracker.intro_message && (
        <Reveal index={2}>
          <p className="body-text" style={{ paddingTop: 36, maxWidth: "66ch", fontSize: 16, lineHeight: 1.7 }}>
            {tracker.intro_message}
          </p>
        </Reveal>
      )}

      {/* What we're still collecting, one line per list. The lists themselves
          live in Client to do — this is the summary that sends you there, and
          the place a VEXOS project visibly differs from a visualization one. */}
      <Section
        index={2}
        label="Your to-do list"
        title={
          !files.has_checklist ? "Your list is being prepared"
            : files.files_complete ? "You're all set"
            : `${files.outstanding_required ?? 0} item${(files.outstanding_required ?? 0) === 1 ? "" : "s"} still needed`
        }
        note={
          !files.has_checklist ? "Your project manager is putting together the list of what we need. Please check back shortly."
            : files.files_complete ? "Every required item is in. You can still add or replace files at any time."
            : `You've provided ${files.required_complete} of ${files.required_total} required items. Your project clock starts when they're all in — your vendors can use this same link.`
        }
      >
        {/* One row per list, each opening its own list — never a merged view. */}
        {(tracker.checklists || []).map((c, i) => (
          <button key={c.kind} onClick={() => openTodo(c.kind)}
            className="w-full text-left flex flex-wrap items-baseline gap-x-5 gap-y-1 group"
            style={{ paddingTop: i === 0 ? 0 : 18, paddingBottom: 18, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="flex-1" style={{ minWidth: 200 }}>
              <span className="inline-flex items-center gap-2">
                <span style={{ fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.1px", color: "var(--ink)" }}>
                  {c.kind_label}
                </span>
                <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-250"
                  strokeWidth={1.5} style={{ color: "var(--estate)" }} />
              </span>
              {/* The required count is the one that gates the project clock,
                  and it is the only one a client has to act on — the 44-item
                  total beside it only ever read as a bigger job. Plain type,
                  to match the list this row opens. */}
              <p style={{
                marginTop: 5, fontSize: 12.5, fontWeight: 300, letterSpacing: "-0.1px",
                color: c.required_complete >= c.required_total ? "var(--estate)" : "var(--tertiary)",
              }}>
                {c.required_complete >= c.required_total
                  ? "Everything required is in"
                  : `${c.required_complete} of ${c.required_total} required items in`}
              </p>
              <CompletionBar percent={c.completion_percentage} />
            </div>
            <span className="tabular shrink-0" style={{
              fontSize: 22, fontWeight: 300, letterSpacing: "-0.6px",
              color: c.completion_percentage === 100 ? "var(--estate)" : "var(--ink)",
            }}>
              {c.completion_percentage}%
            </span>
          </button>
        ))}
      </Section>

      <Section
        index={3}
        label="Phases"
        title="Where your project stands"
        note={tracker.phase_blurb}
      >
        <PhaseRail phases={tracker.phases || []} onHold={onHold} />
      </Section>

      <Section
        index={4}
        label={isClayMilestones ? "Visualization delivery" : "Delivery"}
        title="Two rounds of revision, every project"
        note={(tracker.rounds || []).some((r) => r.key === "clay")
          ? "A first look in clay, then your first draft colorized and materialized, then two rounds of your notes — the first becomes V2, the second becomes your final files."
          : "A first look, then two rounds of your notes — the first becomes V2, the second becomes your final files."}
      >
        <DeliveryProcess rounds={tracker.rounds || []} highlight={highlight} />
      </Section>

      {/* The platform's own milestones, with their own links. Without this a
          client who was emailed “VEXOS Milestone One is ready” had nowhere on
          the page to land and nothing to click — the VEXOS rail above states a
          status and stops there. VEXOS counts its own milestones; it never
          borrows the visualization rail's V1 / V2 / Final.

          Every label and blurb here comes from the server already worded for
          the terms THIS client was sold, so the only thing the page decides is
          its own section copy — hence `isClayMilestones`. A project sold before
          the rename must keep reading the way its proposal did. */}
      {tracker.has_vexos && (tracker.vexos_rounds || []).length > 0 && (
        <Section
          index={4}
          label="VEXOS delivery"
          title={isClayMilestones ? "Your platform, milestone by milestone" : "Your platform, round by round"}
          note={isClayMilestones
            ? "The platform is built and reviewed alongside your visualization milestones, and counted separately from them. Same two rounds of notes."
            : "The platform is built and reviewed alongside your renders. Same two rounds of notes."}
          tone="ink"
        >
          <DeliveryProcess
            rounds={tracker.vexos_rounds || []}
            highlight={highlight}
            openLabel="Open the platform"
            variant="vexos"
          />
        </Section>
      )}

      {tracker.payments?.has_schedule && (
        <Section
          index={5}
          label="Milestones and payments"
          title={
            payments.summary.outstanding_total > 0
              ? `${payments.summary.outstanding_total_text} remaining`
              : "Fully paid"
          }
          note={
            payments.summary.outstanding_total > 0
              ? `${payments.summary.paid_count} of ${payments.summary.total_count} payment milestones settled. Each one is tied to a stage of your project, so you always know what's been invoiced and what's still ahead.`
              : "Every payment milestone on this project is settled. Thank you."
          }
        >
          <PaymentsBlock payments={payments} onPay={onPay} canPay={tracker.can_pay_online} />
        </Section>
      )}

      {tracker.subscription?.has_subscription && (
        <Section
          index={6}
          label="Billing"
          title={
            tracker.subscription.pending_delivery
              ? "Your platform fee"
              : `${tracker.subscription.monthly_amount_text} a month`
          }
          note={
            tracker.subscription.delivered_not_started
              ? `Your platform fee is paid in ${tracker.subscription.term_months} monthly installments of ${tracker.subscription.monthly_amount_text}, invoiced from your platform launch date. We'll confirm the billing dates with you.`
              : tracker.subscription.pending_delivery
                ? `Once your VEXOS platform is live, your platform fee is paid in ${tracker.subscription.term_months} monthly installments of ${tracker.subscription.monthly_amount_text}, invoiced from your platform launch date.`
                : `${tracker.subscription.term_months} months from ${
                    tracker.subscription.billing_start_text || "your platform launch date"
                  }, paid in monthly installments of your platform fee. This is separate from your project payments above.`
          }
        >
          <BillingBlock subscription={tracker.subscription} />
        </Section>
      )}

      {(tracker.updates || []).length > 0 && (
        <Section index={7} label="Updates" title="Latest from your team">
          <div>
            {tracker.updates.map((u, i) => (
              <div key={u.id} style={{
                paddingTop: i === 0 ? 0 : 20, paddingBottom: 20,
                borderTop: i === 0 ? "none" : "1px solid var(--hairline)",
              }}>
                <p className="micro-label">{u.created_text}{u.author_name ? ` · ${u.author_name}` : ""}</p>
                <p className="body-text" style={{ marginTop: 10, maxWidth: "68ch" }}>{u.body}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {running && (
        <Reveal index={8}>
          <p className="body-text" style={{ marginTop: 48, maxWidth: "68ch", color: "var(--tertiary)" }}>
            Need a change, or have a new request? Reach out to your project manager. New requests after this
            point may shift the delivery window.
          </p>
        </Reveal>
      )}
    </div>
  );
}

/* ── Right rail ───────────────────────────────────────────────────────────── */

function RightRail({ tracker, setPage }) {
  const next = (tracker.upcoming_meetings || [])[0];

  return (
    <div className="flex flex-col" style={{ gap: 22 }}>
      <NextMeetingCard meeting={next} />

      {/* Below `lg` there is no left rail, so the numbers live here as tiles.
          At `lg` and up the rail has them, and a second copy in the margin
          would be the same four figures twice on one screen. */}
      <div className="lg:hidden grid grid-cols-2" style={{ gap: 12 }}>
        {projectStats(tracker).map((s) => (
          <StatTile key={s.key} tint={s.tint} value={s.value} label={s.label} detail={s.detail} />
        ))}
      </div>

      <div style={{ padding: "20px 22px", borderRadius: R.panel, backgroundColor: "var(--surface)", border: "1px solid var(--hairline)" }}>
        <MiniCalendar events={tracker.calendar_events || []} />
      </div>

      {(tracker.documents || []).length > 0 && (
        <div style={{ padding: "20px 22px", borderRadius: R.panel, backgroundColor: "var(--surface)", border: "1px solid var(--hairline)" }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
            <p className="micro-label">Documents</p>
            <button className="link-estate" style={{ fontSize: 12.5 }} onClick={() => setPage("documents")}>View all</button>
          </div>
          {(tracker.documents || []).slice(0, 3).map((d, i) => (
            <div key={d.id} className="flex items-center gap-3"
              style={{ paddingTop: 12, paddingBottom: i === 2 ? 0 : 12, borderBottom: i >= Math.min(2, (tracker.documents || []).length - 1) ? "none" : "1px solid var(--hairline)" }}>
              <FileText className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} style={{ color: "var(--tertiary)" }} />
              <span className="truncate" style={{ fontSize: 13, fontWeight: 300, color: "var(--body-dark)" }}>{d.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Meetings page ────────────────────────────────────────────────────────── */

function MeetingsPage({ tracker }) {
  const [openId, setOpenId] = useState(null);
  const upcoming = tracker.upcoming_meetings || [];
  const meetings = tracker.meetings || [];

  return (
    <div>
      {upcoming.length > 0 && (
        <Section index={0} tight label="Booked" title="Coming up">
          <div>
            {upcoming.map((m, i) => (
              <div key={m.id} className="flex flex-wrap items-baseline gap-x-6 gap-y-2"
                style={{ paddingTop: i === 0 ? 0 : 18, paddingBottom: 18, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                <p className="tabular shrink-0" style={{ fontSize: 13.5, fontWeight: 300, color: "var(--estate)", width: 150 }}>
                  {m.is_today ? "Today" : m.date_text} · {m.time_text}
                </p>
                <div className="flex-1 min-w-0" style={{ minWidth: 200 }}>
                  <p style={{ fontSize: 15, fontWeight: 400, letterSpacing: "-0.2px", color: "var(--ink)" }}>{m.title}</p>
                  {m.note && <p className="body-text" style={{ marginTop: 5 }}>{m.note}</p>}
                </div>
                {m.join_url && (
                  <a className="link-estate shrink-0" href={m.join_url} target="_blank" rel="noreferrer">Join</a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section
        index={upcoming.length ? 1 : 0}
        tight={!upcoming.length}
        label="Meetings"
        title={meetings.length ? "Every conversation, on the record" : "No meetings yet"}
        note={meetings.length
          ? "Recorded meetings about your project land here automatically — the recording and the notes — so there's never a question of what was said or when."
          : "Once we've met about your project, every recorded conversation will collect here automatically."}
      >
        <div>
          {meetings.map((m, i) => {
            const open = openId === m.id;
            return (
              <div key={m.id} style={{ paddingTop: i === 0 ? 0 : 20, paddingBottom: 20, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <p className="tabular shrink-0" style={{ fontSize: 13.5, fontWeight: 300, color: "var(--quiet)", width: 150 }}>
                    {m.date_text}{m.time_text ? ` · ${m.time_text}` : ""}
                  </p>
                  <div className="flex-1 min-w-0" style={{ minWidth: 200 }}>
                    <p style={{ fontSize: 15, fontWeight: 400, letterSpacing: "-0.2px", color: "var(--ink)" }}>{m.title}</p>
                    <p className="micro-label" style={{ marginTop: 6 }}>
                      {[m.duration_text, (m.participants || []).slice(0, 4).join(", ")].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {/* The recording sits alongside the notes, not instead of
                      them. A summary answers "what was decided"; only the call
                      itself answers "what did they actually say". */}
                  <div className="flex items-center gap-5 shrink-0">
                    {m.recording_url && (
                      <a href={m.recording_url} target="_blank" rel="noreferrer"
                        className="micro-label inline-flex items-center gap-1.5" style={{ color: "var(--estate)" }}>
                        <Video className="w-3.5 h-3.5" strokeWidth={1.5} /> Watch
                      </a>
                    )}
                    {m.summary_text && (
                      <button onClick={() => setOpenId(open ? null : m.id)}
                        className="micro-label inline-flex items-center gap-1.5" style={{ color: "var(--estate)" }}>
                        {open ? <>Hide notes <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} /></>
                          : <>Notes <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} /></>}
                      </button>
                    )}
                  </div>
                </div>
                {open && m.summary_text && (
                  <div style={{ marginTop: 16, marginLeft: 0, padding: "18px 22px", backgroundColor: "var(--image-well)", borderRadius: R.well }}>
                    {m.summary_text.split("\n").map((line, li) => (
                      <p key={li} className="body-text" style={{ marginTop: li === 0 ? 0 : 7, maxWidth: "72ch" }}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

/* ── Documents page ───────────────────────────────────────────────────────── */

function DocumentsPage({ tracker, token }) {
  const [openDocId, setOpenDocId] = useState(null);
  const documents = tracker.documents || [];
  const links = tracker.quick_links || [];

  const fileUrl = (d, download) =>
    `${API}/project-tracker/client/${token}/documents/${d.id}/file${download ? "?download=true" : ""}`;

  return (
    <div>
      <Section
        index={0}
        tight
        label="Documents"
        title={documents.length ? "Your project documents" : "Nothing shared yet"}
        note={documents.length
          ? "Proposals, schedules, guides — everything we've shared with you, in one place for the life of the project."
          : "Anything we share with you — proposals, schedules, drawings — will appear here."}
      >
        <div>
          {documents.map((d, i) => {
            const open = openDocId === d.id;
            return (
              <div key={d.id} style={{ paddingTop: i === 0 ? 0 : 20, paddingBottom: 20, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                <div className="flex items-start gap-4">
                  <span className="inline-flex items-center justify-center shrink-0"
                    style={{ width: 40, height: 40, borderRadius: R.well, backgroundColor: "var(--image-well)", border: "1px solid var(--hairline)" }}>
                    {d.type === "link"
                      ? <LinkIcon className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--tertiary)" }} />
                      : <FileText className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--tertiary)" }} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 15, fontWeight: 400, letterSpacing: "-0.2px", color: "var(--ink)" }}>{d.title}</p>
                    <p className="micro-label" style={{ marginTop: 6 }}>
                      {[d.category, d.size_text, d.added_text ? `added ${d.added_text}` : null].filter(Boolean).join(" · ")}
                    </p>
                    {d.description && <p className="body-text" style={{ marginTop: 8, maxWidth: "62ch" }}>{d.description}</p>}
                  </div>
                  <div className="flex items-center gap-4 shrink-0" style={{ paddingTop: 4 }}>
                    {d.type === "pdf" && (
                      <>
                        <a className="link-estate inline-flex items-center gap-1.5" href={fileUrl(d)} target="_blank" rel="noreferrer">
                          View <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        <a className="micro-label inline-flex items-center gap-1.5" style={{ color: "var(--tertiary)" }}
                          href={fileUrl(d, true)}>
                          <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                      </>
                    )}
                    {d.type === "link" && d.link_url && (
                      <a className="link-estate inline-flex items-center gap-1.5" href={d.link_url} target="_blank" rel="noreferrer">
                        Open <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </a>
                    )}
                    {d.type === "document" && d.text_content && (
                      <button className="link-estate" onClick={() => setOpenDocId(open ? null : d.id)}>
                        {open ? "Hide" : "Read"}
                      </button>
                    )}
                  </div>
                </div>
                {open && d.text_content && (
                  <div className="tracker-doc-body" style={{ marginTop: 16, padding: "22px 26px", backgroundColor: "var(--surface)", border: "1px solid var(--hairline)", borderRadius: R.well }}
                    dangerouslySetInnerHTML={{ __html: d.text_content }} />
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {links.length > 0 && (
        <Section index={1} label="Links" title="Shared links">
          <div>
            {links.map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-4"
                style={{ paddingTop: i === 0 ? 0 : 16, paddingBottom: 16, borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                <span style={{ fontSize: 14.5, fontWeight: 300, color: "var(--body-dark)" }}>{l.label}</span>
                <a className="link-estate inline-flex items-center gap-1.5 shrink-0" href={l.url} target="_blank" rel="noreferrer">
                  Open <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                </a>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ── Your team ─────────────────────────────────────────────────────────────
 *
 * Two halves of one question — "who do I talk to?" — that used to have no
 * single answer anywhere on this page.
 *
 * OURS is three named people with faces: the project lead who runs the work,
 * the VEXOS manager who builds the platform, and the sales rep who wrote the
 * agreement. A client's whole relationship with us is those three humans, and
 * before this the portal named one of them, in eight-point type, in the margin.
 *
 * THEIRS is a roster of ROLES rather than a blank "add someone" box. A client
 * asked to remember every consultant on a project they are in the middle of
 * running mostly adds nobody; a row that says "Architect — add" is a form to
 * fill in. The people they name here are also the people they can hand an item
 * to on the to-do list, which is the whole reason the roster earns its place
 * rather than being an address book nobody maintains.
 */

function ContactCard({ contact }) {
  return (
    <div style={{
      padding: "24px 26px", borderRadius: R.panel,
      backgroundColor: "var(--surface)", border: "1px solid var(--hairline)",
    }}>
      <div className="flex items-center gap-4">
        <Avatar person={contact} size={58} />
        <div className="min-w-0">
          <p style={{ fontSize: 16, fontWeight: 400, letterSpacing: "-0.3px", color: "var(--ink)" }}>
            {contact.name}
          </p>
          <p className="micro-label" style={{ marginTop: 5 }}>{contact.role}</p>
        </div>
      </div>

      {contact.blurb && (
        <p className="body-text" style={{ marginTop: 16 }}>{contact.blurb}</p>
      )}

      {/* The whole point of the card. A mailto and a tel, set as rows rather
          than buttons — this is a letterhead, not a toolbar. */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--hairline)" }}>
        {contact.email && (
          <a className="flex items-center gap-2.5 link-estate" href={`mailto:${contact.email}`}
            style={{ fontSize: 13.5, fontWeight: 300 }}>
            <Mail className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a className="flex items-center gap-2.5 link-estate" href={`tel:${contact.phone_href || contact.phone}`}
            style={{ fontSize: 13.5, fontWeight: 300, marginTop: contact.email ? 9 : 0 }}>
            <Phone className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            <span className="truncate">{contact.phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

/* One role on the client's own side: filled, or an invitation to fill it.
 *
 * The form opens inside the row it belongs to rather than at the top of the
 * section, so the name being typed is under the label of the role it answers —
 * "Architect", then the box. A single shared form above a list of roles is how
 * you end up filing the landscape architect under Architect. */
function RoleRow({ role, members, first, waiting, busy, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const repeatable = role.key === "other";
  const filled = members.length > 0;

  const submit = async () => {
    if (!name.trim() || !email.trim()) return;
    const ok = await onAdd({
      name: name.trim(), email: email.trim(), role: role.key,
      title: title.trim() || null,
    });
    if (ok) { setName(""); setEmail(""); setTitle(""); setOpen(false); }
  };

  const field = "w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate";

  return (
    <div style={{
      paddingTop: first ? 0 : 18, paddingBottom: 18,
      borderTop: first ? "none" : "1px solid var(--hairline)",
    }}>
      <div className="flex items-start gap-4">
        <span className="shrink-0 inline-flex items-center justify-center"
          style={{
            width: 38, height: 38, borderRadius: 999,
            backgroundColor: filled ? "transparent" : "var(--image-well)",
            border: `1px ${filled ? "solid" : "dashed"} var(--hairline-strong)`,
          }}>
          {filled
            ? <Check className="w-4 h-4" strokeWidth={1.75} style={{ color: "var(--estate)" }} />
            : <UserPlus className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--tertiary)" }} />}
        </span>

        <div className="flex-1 min-w-0">
          <p style={{ fontSize: 14.5, fontWeight: 400, letterSpacing: "-0.1px", color: "var(--ink)" }}>
            {role.label}
          </p>
          {!filled && (
            <p className="body-text" style={{ marginTop: 4, color: "var(--tertiary)" }}>{role.hint}</p>
          )}

          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3" style={{ marginTop: 10 }}>
              <Avatar person={m} size={28}
                style={{ backgroundColor: "var(--image-well)", color: "var(--body)" }} />
              <div className="min-w-0 flex-1">
                <p style={{ fontSize: 14, fontWeight: 300, color: "var(--body-dark)" }}>{m.name}</p>
                <p className="micro-label" style={{ marginTop: 3 }}>
                  {/* What they owe, in words. A client's own list of who they
                      are waiting on is the reason to keep this page open.
                      Their title only on the open-ended row — everywhere else
                      it is the heading directly above them. */}
                  {[
                    repeatable && m.title ? m.title : null,
                    m.email,
                    waiting[m.id]
                      ? `asked for ${waiting[m.id]} item${waiting[m.id] === 1 ? "" : "s"}`
                      : null,
                  ].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button onClick={() => onRemove(m)} aria-label={`Remove ${m.name}`}
                disabled={busy === m.id} className="shrink-0">
                {busy === m.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />
                  : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--quiet)" }} />}
              </button>
            </div>
          ))}

          {open && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, maxWidth: 520 }}>
              <div className="flex flex-col sm:flex-row gap-2">
                <input className={field} value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Their name" autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
                <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Their email"
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
              </div>
              {/* Only on the open-ended row. The five named roles have already
                  said what the person does; asking again would be a box whose
                  right answer is the label directly above it. */}
              {repeatable && (
                <input className={field} value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="What they do (optional) — structural engineer, planner…"
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
              )}
              <div className="flex items-center gap-3">
                <button className="btn-primary" style={{ padding: "9px 22px", fontSize: 13.5 }}
                  onClick={submit} disabled={busy === role.key || !name.trim() || !email.trim()}>
                  {busy === role.key ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : null}
                  Add and send the link
                </button>
                <button className="link-estate" style={{ fontSize: 13 }} onClick={() => setOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {!open && (!filled || repeatable) && (
          <button className="btn-secondary shrink-0 inline-flex items-center gap-2 whitespace-nowrap"
            style={{ padding: "8px 18px", fontSize: 13 }}
            onClick={() => setOpen(true)}>
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
            Add
          </button>
        )}
      </div>
    </div>
  );
}

function TeamPage({ tracker, token, checklists, refresh, openTodo }) {
  const [busy, setBusy] = useState(null);
  const contacts = tracker.contacts || [];
  const roles = tracker.team_roles || [];
  const members = tracker.team_members || [];

  // How many open items each person has been asked for. Counted off the same
  // lists the to-do page renders, so the two can't disagree; completed items
  // drop out, because "asked for 4 items" against a person who has sent three
  // of them is a number that makes a client chase the wrong person.
  const waiting = useMemo(() => {
    const out = {};
    Object.values(checklists || {}).forEach((cl) => {
      (cl?.sections || []).forEach((sec) => {
        (sec.items || []).forEach((item) => {
          const id = item.assignee?.member_id;
          if (!id || item.status === "complete" || item.status === "n_a") return;
          out[id] = (out[id] || 0) + 1;
        });
      });
    });
    return out;
  }, [checklists]);

  // Anyone added before the roles existed — or through an older version of this
  // form — has no role key. They are still on the project, so they belong on
  // the "someone else" row rather than quietly vanishing off the page.
  const byRole = (key) =>
    members.filter((m) => (m.role || "other") === key);

  const addMember = async ({ name, email, role, title }) => {
    setBusy(role);
    try {
      await axios.post(`${API}/project-tracker/client/${token}/team`, {
        name, email, role, title: title || null,
      });
      toast.success(`Done — we've emailed ${name.split(" ")[0]} the dashboard link`);
      await refresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add them. Please try again.");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const removeMember = async (m) => {
    setBusy(m.id);
    try {
      await axios.delete(`${API}/project-tracker/client/${token}/team/${m.id}`);
      await refresh();
    } catch (e) {
      toast.error("Couldn't remove them. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {contacts.length > 0 && (
        <Section
          index={0}
          tight
          label="The Render Pros"
          title="Your team here"
          note="The people looking after your project. Email or call any of them directly — you never need to go through anybody."
        >
          <div className="grid md:grid-cols-2 xl:grid-cols-3" style={{ gap: 18 }}>
            {contacts.map((c) => <ContactCard key={c.key} contact={c} />)}
          </div>
        </Section>
      )}

      <Section
        index={1}
        label="Your side"
        title="Who else is on this project"
        note="Add the people working with you and they get this same dashboard link. Once they're here you can pass items on the to-do list straight to them, so you're not the one collecting everything."
      >
        <div>
          {roles.map((role, i) => (
            <RoleRow
              key={role.key}
              role={role}
              first={i === 0}
              members={byRole(role.key)}
              waiting={waiting}
              busy={busy}
              onAdd={addMember}
              onRemove={removeMember}
            />
          ))}
        </div>

        {members.length > 0 && (
          <p className="body-text" style={{ marginTop: 20 }}>
            Everyone here sees the same dashboard you do.{" "}
            <button className="link-estate" style={{ fontSize: "inherit" }} onClick={() => openTodo(null)}>
              Hand them something from your to-do list
            </button>
            .
          </p>
        )}
      </Section>
    </div>
  );
}

/* ── Settings page (profile + team) ───────────────────────────────────────── */

function SettingsPage({ tracker, token, profile, setProfile, setPage }) {
  const [name, setName] = useState(profile.name || "");
  const [email, setEmail] = useState(profile.email || "");

  const saveDetails = () => {
    const next = { name: name.trim(), email: email.trim() };
    setProfile(next);
    saveProfile(token, next);
    toast.success("Saved — the dashboard will greet you by name");
  };

  const inputCls = "w-full bg-surface border border-border rounded-lg px-3.5 py-2.5 text-[15px] font-light text-foreground placeholder:text-ink-400 focus:outline-none focus:border-estate";

  return (
    <div>
      <Section
        index={0}
        tight
        label="Profile"
        title="Your details"
        note="Just so the dashboard knows who it's talking to. Stored on this device only — nothing is shared."
      >
        <div className="grid sm:grid-cols-2" style={{ gap: 14, maxWidth: 560 }}>
          <div>
            <p className="micro-label" style={{ marginBottom: 8 }}>Your name</p>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" />
          </div>
          <div>
            <p className="micro-label" style={{ marginBottom: 8 }}>Your email</p>
            <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
        </div>
        <button className="btn-primary" style={{ marginTop: 20 }} onClick={saveDetails}>Save details</button>
      </Section>

      {/* The roster itself lives on its own page now, beside the people the
          client can actually reach. What stays here is the one thing Settings
          is for: a pointer, so somebody who came looking in the obvious wrong
          place is not told nothing. */}
      <Section
        index={1}
        label="Team"
        title="Who's on this project"
        note="Everyone you add gets this same dashboard link, and you can pass to-do items straight to them."
        right={
          <button className="btn-secondary shrink-0 inline-flex items-center gap-2 whitespace-nowrap"
            style={{ padding: "9px 20px", fontSize: 13.5 }}
            onClick={() => setPage("team")}>
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
            Your team
          </button>
        }
      >
        <p className="body-text">
          {(tracker.team_members || []).length === 0
            ? "It's just you so far."
            : `${(tracker.team_members || []).length} ${(tracker.team_members || []).length === 1 ? "person has" : "people have"} been added so far.`}
          {" "}Your team is where you add them, and where you'll find your project lead's details.
        </p>
      </Section>

      <Section
        index={2}
        label="Access"
        title="How access works"
        note="There's no password to remember. This private link is the key to your project — everyone you add receives the same link, and it works for the life of the project. If it should ever stop working, your project lead can issue a fresh one."
      />
    </div>
  );
}

/* ── Client to-do ─────────────────────────────────────────────────────────── */

// The sections a client actually sees, and the one seam that decides it. A
// section whose every item is internal-only comes back from the API with a zero
// completion, which reads as "unfinished" forever: it would open by default
// showing an empty body and stop the accordion ever handing off to the next
// section. There is no such thing as an empty step, so it isn't a step. Both
// the accordion and the count above it read this, so they can't disagree.
const clientSections = (checklist) =>
  (checklist?.sections || []).filter((s) => (s.items || []).length > 0);

/* ── What this section looks like on a finished page ───────────────────────
 *
 * A client was being asked for a "hero headline" and a "project quote" with no
 * picture of where any of it lands — filling in a form for a page they had
 * never seen. These are screenshots of a delivered landing page, one or two
 * per section, sitting between the section's description and its questions.
 *
 * Shown small and clickable rather than large: at the width of the accordion
 * a full screenshot is a thumbnail with unreadable type, so the job of the
 * inline image is to say "this part of the page", and the job of the tap is to
 * let them actually read it.
 */
function ReferenceShots({ images }) {
  const shots = images || [];
  const [zoom, setZoom] = useState(null);

  // Esc closes, and the page behind stops scrolling while it's open.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [zoom]);

  if (!shots.length) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      <p className="micro-label" style={{ marginBottom: 10 }}>
        For reference — this section on a page we've delivered
      </p>

      <div className="flex flex-col" style={{ gap: 14 }}>
        {shots.map((shot) => (
          <figure key={shot.src} style={{ margin: 0 }}>
            <button
              type="button"
              onClick={() => setZoom(shot)}
              className="block w-full"
              style={{
                borderRadius: R.well,
                overflow: "hidden",
                border: "1px solid var(--hairline)",
                backgroundColor: "var(--image-well)",
                lineHeight: 0,
              }}
              aria-label={`Enlarge: ${shot.caption}`}
            >
              {/* width/height are the REAL pixel dimensions, so the browser
                  reserves the right box before the file arrives. Without them
                  a lazy image is 0px tall and the caption and every question
                  under it jump down the moment it loads. */}
              <img
                src={shot.src}
                alt={shot.caption}
                width={shot.width}
                height={shot.height}
                loading="lazy"
                className="block w-full"
                style={{ height: "auto" }}
              />
            </button>
            <figcaption
              className="body-text"
              style={{ marginTop: 8, fontSize: 12.5, color: "var(--tertiary)", maxWidth: "62ch" }}
            >
              {shot.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      {/* Portaled to the body on purpose. Every section on this page animates
          in under a `transform`, and a transformed ancestor is a containing
          block for `position: fixed` — so an overlay rendered in place covers
          the accordion and nothing else, leaving the sidebar and the rail
          sitting on top of it. */}
      {zoom && createPortal(
        <div
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label={zoom.caption}
          className="fixed inset-0 flex flex-col items-center justify-center"
          style={{
            zIndex: 120,
            backgroundColor: "rgba(20, 19, 17, 0.88)",
            padding: "24px 16px",
            cursor: "zoom-out",
          }}
        >
          <img
            src={zoom.src}
            alt={zoom.caption}
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 110px)",
              borderRadius: R.well,
              objectFit: "contain",
            }}
          />
          <p style={{
            marginTop: 14, maxWidth: "62ch", textAlign: "center",
            fontSize: 13, fontWeight: 300, lineHeight: 1.55, color: "rgba(255,255,255,0.78)",
          }}>
            {zoom.caption}
          </p>
          <button
            type="button"
            onClick={() => setZoom(null)}
            aria-label="Close"
            className="absolute flex items-center justify-center"
            style={{
              top: 16, right: 16, width: 36, height: 36, borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.12)",
            }}
          >
            <X className="w-4 h-4" strokeWidth={1.5} style={{ color: "#fff" }} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

// One checklist as a numbered accordion — one section open at a time, the rest
// collapsed to a single line each. Forty-four fields in one scroll is the thing
// that overwhelms; a section of five with its own bar is a task you can finish.
//
// There are deliberately no To do/Completed tabs. Two competing counts on one
// screen ("6 required outstanding" beside "To do 27") read as two different
// jobs. Completed items stay in place with a check, so there is exactly one
// number per section and one per list.
function ChecklistBlock({ checklist, ops, members, onOpenTeam }) {
  const sections = useMemo(() => clientSections(checklist), [checklist]);

  // The section to sit on: the first one that isn't finished.
  const firstUnfinished = useMemo(() => {
    const i = sections.findIndex((s) => (s.completion?.percentage ?? 0) < 100);
    return i === -1 ? null : sections[i]?.id;
  }, [sections]);

  const [openId, setOpenId] = useState(null);
  const [touched, setTouched] = useState(false);
  const prevDone = useRef({});

  // Follow the work forward. Until the client opens something themselves we
  // just track the first unfinished section; after that we only move them on
  // when the section they're in actually crosses into complete, so we never
  // yank a section closed while they're reading it.
  useEffect(() => {
    if (!sections.length) return;
    const done = {};
    sections.forEach((s) => { done[s.id] = (s.completion?.percentage ?? 0) >= 100; });
    const justFinished = openId && done[openId] && prevDone.current[openId] === false;
    prevDone.current = done;

    if (!touched) { setOpenId(firstUnfinished); return; }
    if (justFinished) setOpenId(firstUnfinished);
  }, [sections, firstUnfinished, openId, touched]);

  if (!checklist) return <p className="micro-label">Loading this list</p>;

  return (
    <div>
      {sections.map((s, i) => {
        const c = s.completion || { total: 0, complete: 0, percentage: 0 };
        const done = c.percentage >= 100 && c.total > 0;
        const started = c.complete > 0 && !done;
        // Only MUST-HAVE items gate the project clock, and a client reading
        // "5 items still needed" over eight sections adding up to forty-four
        // fields has no way to tell which of them are the five. A section with
        // nothing required says so, so the eye can skip it.
        const optional = !(s.items || []).some((it) => it.required);
        const open = s.id === openId;
        // A rule between two collapsed rows only. The open section is a raised
        // card, and a card wants air around it, not a line welded to its edge.
        const rule = i > 0 && !open && sections[i - 1].id !== openId;
        return (
          <div key={s.id}
            style={{
              borderTop: rule ? "1px solid var(--hairline)" : "none",
              backgroundColor: open ? "var(--surface)" : "transparent",
              borderRadius: open ? R.well : 0,
              margin: open ? "12px 0" : 0,
              transition: "background-color .3s var(--ease-brand)",
            }}>
            <button
              onClick={() => { setTouched(true); setOpenId(open ? null : s.id); }}
              className="todo-row w-full text-left flex items-center gap-4"
              style={{ padding: open ? "20px 22px 16px" : "18px 6px" }}
              aria-expanded={open}
            >
              {/* One mark carries both the step number and its state, so the
                  row has a single thing to read on the left instead of a
                  numeral, a tick and a bar all saying the same thing. */}
              <span className="shrink-0 flex items-center justify-center tabular"
                style={{
                  width: 26, height: 26, borderRadius: 999,
                  backgroundColor: done ? "var(--estate)" : "transparent",
                  border: done ? "none" : `1px solid ${started || open ? "var(--estate)" : "var(--handle)"}`,
                  fontSize: 11.5, fontWeight: 400, lineHeight: 1,
                  color: started || open ? "var(--estate)" : "var(--quiet)",
                  transition: "background-color .3s var(--ease-brand), border-color .3s var(--ease-brand)",
                }}>
                {done ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} style={{ color: "#fff" }} /> : i + 1}
              </span>

              <span className="flex-1 min-w-0">
                <span className="block truncate" style={{
                  fontSize: 15.5, letterSpacing: "-0.2px", lineHeight: 1.35,
                  fontWeight: open ? 400 : 300,
                  color: done ? "var(--body)" : "var(--ink)",
                }}>
                  {s.name}
                </span>
                {/* Said in words. "0 OF 5" in caps beside a bar that reads as a
                    stray underline is the thing that made eight of these hard
                    to look at. */}
                <span className="block" style={{
                  marginTop: 3, fontSize: 12.5, fontWeight: 300, letterSpacing: "-0.1px",
                  color: done ? "var(--estate)" : "var(--tertiary)",
                }}>
                  {done ? "Done"
                    : started ? `${c.complete} of ${c.total} done`
                    : `${c.total} item${c.total === 1 ? "" : "s"}`}
                  {!done && optional && (
                    <span style={{ color: "var(--quiet)" }}> · optional</span>
                  )}
                </span>
              </span>

              <ChevronDown className="w-4 h-4 shrink-0" strokeWidth={1.5}
                style={{
                  color: "var(--tertiary)",
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform .25s var(--ease-brand)",
                }} />
            </button>

            {open && (
              <div style={{ padding: "0 22px 22px" }}>
                {s.description && (
                  <p className="body-text" style={{ marginBottom: 18, maxWidth: "62ch" }}>{s.description}</p>
                )}
                <ReferenceShots images={s.reference_images} />
                {(s.items || []).map((item, ii) => (
                  <ChecklistItem
                    key={item.id}
                    item={item}
                    first={ii === 0}
                    busy={ops.busyItem === item.id}
                    members={members}
                    onAddLink={ops.addLink}
                    onRemoveLink={ops.removeLink}
                    onSave={ops.saveValue}
                    onAssign={ops.assign}
                    onOpenTeam={onOpenTeam}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A thin completion bar — the brand's 1px rule, warm track, estate indicator.
function CompletionBar({ percent }) {
  return (
    <div className="track" style={{ marginTop: 14 }}>
      <div className="track-fill" style={{ width: `${Math.min(100, Math.max(0, percent || 0))}%` }} />
    </div>
  );
}

// These are genuinely different lists — the VEXOS intakes collect the platform
// content and the landing page copy, visualization collects the source files a
// render is built from (the Project Intake Guide). They are separate records
// with separate tokens and separate percentages, and the client sees ONE at a
// time: stacking them reads as a single long list that happens to start with
// VEXOS.
function ClientToDoPage({ tracker, checklists, opsFor, kind, setKind, onOpenTeam }) {
  const lists = tracker.checklists || [];

  if (!lists.length) {
    return (
      <Section index={0} label="Your to-do list" title="Your list is being prepared"
        note="Your project manager is putting together the list of what we need. Please check back shortly." />
    );
  }

  const meta = lists.find((l) => l.kind === kind) || lists[0];
  const data = checklists[meta.token];
  const outstanding = Math.max(0, (meta.required_total || 0) - (meta.required_complete || 0));
  const visibleSections = clientSections(data);
  const sectionCount = visibleSections.length;
  const sectionsDone = visibleSections.filter((s) => (s.completion?.percentage ?? 0) >= 100).length;

  return (
    <>
      {/* One switch per list the project sold. A visualization-only project
          never sees it — there's nothing to switch between. */}
      {lists.length > 1 && (
        <Reveal index={0}>
          <div className="inline-flex" style={{
            marginTop: 32, padding: 4, gap: 4, borderRadius: 999,
            backgroundColor: "var(--track-fill)",
          }}>
            {lists.map((l) => {
              const active = l.kind === meta.kind;
              const left = Math.max(0, (l.required_total || 0) - (l.required_complete || 0));
              return (
                <button key={l.kind} onClick={() => setKind(l.kind)}
                  className="inline-flex items-center gap-2"
                  style={{
                    padding: "9px 18px", borderRadius: 999,
                    backgroundColor: active ? "var(--surface)" : "transparent",
                    color: active ? "var(--ink)" : "var(--body)",
                    fontSize: 13.5, fontWeight: active ? 400 : 300, letterSpacing: "-0.2px",
                    transition: "background-color .55s var(--ease-thumb), color .25s var(--ease-brand)",
                  }}>
                  {l.kind_label}
                  {/* A bare number beside a name reads as an item count, not as
                      what is still owed. One word fixes it. */}
                  {left ? (
                    <span className="tabular" style={{ fontSize: 12, fontWeight: 300, color: "var(--terracotta)" }}>
                      {left} left
                    </span>
                  ) : (
                    <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--estate)" }} />
                  )}
                </button>
              );
            })}
          </div>
        </Reveal>
      )}

      <Section
        key={meta.kind}
        index={1}
        label={meta.kind_label}
        title={
          outstanding === 0
            ? "You're all set"
            : `${outstanding} item${outstanding === 1 ? "" : "s"} still needed`
        }
        note={meta.kind_blurb}
        right={
          <div className="shrink-0 text-right" style={{ minWidth: 96 }}>
            <p className="tabular" style={{ fontSize: 30, fontWeight: 300, letterSpacing: "-1px", lineHeight: 1, color: "var(--ink)" }}>
              {meta.completion_percentage}%
            </p>
            <p className="micro-label" style={{ marginTop: 7 }}>Complete</p>
          </div>
        }
      >
        <CompletionBar percent={meta.completion_percentage} />
        {/* One line, counted in steps — the same unit as the list below it.
            Item totals used to be stated here too, which put two different
            numbers for the same work side by side. Set in plain type rather
            than the caps micro-label: it is a sentence a client reads, not a
            column heading. */}
        <p style={{
          marginTop: 12, marginBottom: 22, fontSize: 13, fontWeight: 300,
          letterSpacing: "-0.1px", color: "var(--tertiary)",
        }}>
          {sectionsDone} of {sectionCount} step{sectionCount === 1 ? "" : "s"} complete
        </p>
        <ChecklistBlock checklist={data} ops={opsFor(meta.token)}
          members={tracker.team_members || []} onOpenTeam={onOpenTeam} />
      </Section>
    </>
  );
}

/* ── Page shell ───────────────────────────────────────────────────────────── */

const PAGE_TITLES = {
  todo: "Your to-do list",
  team: "Your team",
  meetings: "Meetings",
  documents: "Documents",
  settings: "Profile & settings",
};

export default function ClientTracker() {
  const { token } = useParams();
  const [tracker, setTracker] = useState(null);
  // Keyed by client token — a project can carry a VEXOS list and a
  // visualization list, and each is fetched against its own token.
  const [checklists, setChecklists] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyItem, setBusyItem] = useState(null);
  const [page, setPage] = useState("dashboard");
  // Which of the project's lists the to-do module is showing. Null means
  // "pick for me" — the first one that still owes us something.
  const [todoKind, setTodoKind] = useState(null);
  const [profile, setProfile] = useState(() => loadProfile(token));
  // The milestone the pay dialog is open against, if any.
  const [payMilestone, setPayMilestone] = useState(null);
  // The round a delivery email pointed at (?delivery=v1). Held in state rather
  // than read from the URL on every render so it can be cleared: the highlight
  // is a "here it is" nudge on arrival, not a permanent state of the page.
  const [deliveryFocus, setDeliveryFocus] = useState(
    () => new URLSearchParams(window.location.search).get("delivery") || null,
  );

  /* Claim the document's horizontal-overflow mode while mounted and hand it
     back on unmount, the way the login page borrows the canvas. Without this
     the app-wide `overflow-x: hidden` makes body a scroll container and the
     sticky sidebar scrolls away with the page. */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("client-portal");
    return () => root.classList.remove("client-portal");
  }, []);

  /* A delivery email's button carries ?delivery=<round key>. Landing means
     three things, and all three have to wait for the tracker to have rendered:
     show the dashboard (the deliveries live there), scroll the round into view,
     and light it up briefly so it's obvious which of the three was meant.
     The parameter is then stripped so a refresh — or a bookmark — doesn't
     replay the animation forever. */
  useEffect(() => {
    if (!deliveryFocus || !tracker) return;
    setPage("dashboard");

    // Poll for the card rather than scrolling on the next frame. Switching to
    // the dashboard is a re-render, the VEXOS rail only mounts when the project
    // has one, and the sections animate in — so one frame later the element a
    // platform link points at frequently does not exist yet, and a scroll that
    // finds nothing fails silently. Ten tries over half a second, then give up:
    // the round is still highlighted, the client just has to scroll to it.
    let tries = 0;
    const seek = window.setInterval(() => {
      const el = document.getElementById(`delivery-${deliveryFocus}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        window.clearInterval(seek);
      } else if (++tries >= 10) {
        window.clearInterval(seek);
      }
    }, 50);

    const url = new URL(window.location.href);
    url.searchParams.delete("delivery");
    window.history.replaceState({}, "", url);
    const fade = setTimeout(() => setDeliveryFocus(null), 6000);
    return () => { window.clearInterval(seek); clearTimeout(fade); };
  }, [deliveryFocus, tracker]);

  const fetchTracker = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/project-tracker/client/${token}`);
      setTracker(res.data);
      // Fetch every list the project carries, in parallel. One failing list
      // must not take the others — or the rest of the page — down with it.
      const metas = res.data.checklists || [];
      const fetched = await Promise.all(
        metas.filter((m) => m.token).map(async (m) => {
          try {
            const cl = await axios.get(`${API}/vexos-checklist/client/${m.token}`);
            return [m.token, cl.data];
          } catch (e) {
            return null;
          }
        }),
      );
      setChecklists(Object.fromEntries(fetched.filter(Boolean)));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't find this project tracker.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTracker(); }, [fetchTracker]);
  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  /* Coming back from Stripe. The redirect itself proves nothing — anyone can
     type that URL — so the SERVER is asked whether the session actually
     cleared, and only its answer is believed. Stripe can take a few seconds to
     settle, hence the short poll rather than a single ask; and the query string
     is cleared either way so a refresh can't replay the whole dance. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("checkout_session_id");
    const cancelled = params.get("checkout_cancelled");
    if (!sessionId && !cancelled) return;

    const clean = () => window.history.replaceState({}, "", window.location.pathname);
    if (cancelled) { clean(); return; }

    let alive = true;
    (async () => {
      for (let attempt = 0; attempt < 6 && alive; attempt++) {
        try {
          const res = await axios.get(
            `${API}/project-tracker/client/${token}/checkout-status/${sessionId}`,
          );
          if (res.data.paid) {
            if (!alive) return;
            toast.success("Payment received — thank you.");
            await fetchTracker();
            clean();
            return;
          }
        } catch (e) {
          if (!alive) return;
          toast.error("We couldn't confirm that payment. If you were charged, tell us and we'll sort it out today.");
          clean();
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (!alive) return;
      // Genuinely still pending — bank transfers via Stripe can take a day.
      toast("Your payment is processing. This page updates as soon as it clears.");
      clean();
    })();
    return () => { alive = false; };
  }, [token, fetchTracker]);

  const refresh = async () => { await fetchTracker(); };

  /* Every write goes to the token of the list the item belongs to, so an
     upload on the visualization list can't land on the VEXOS one. */
  const opsFor = useCallback((clToken) => ({
    busyItem,
    // No status is sent: the server decides whether an answer counts, because
    // the project clock is gated on it and this endpoint is only token-guarded.
    saveValue: async (item, value) => {
      setBusyItem(item.id);
      try {
        await axios.patch(`${API}/vexos-checklist/client/${clToken}/items/${item.id}`, {
          value_json: { value },
        });
        await refresh();
      } catch (e) {
        toast.error("Couldn't save that. Please try again.");
      } finally {
        setBusyItem(null);
      }
    },
    addLink: async (item, { url, label, note }) => {
      setBusyItem(item.id);
      try {
        await axios.post(
          `${API}/vexos-checklist/client/${clToken}/items/${item.id}/links`,
          { url, label: label || null, note: note || null },
        );
        await refresh();
        return true;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't save that link. Please try again.");
        return false;
      } finally {
        setBusyItem(null);
      }
    },
    // Assigning is a client-to-client act: it names an owner and emails them
    // the item. `null` hands it back, which is why it is one call rather than
    // an assign and an unassign that could disagree about what "cleared" means.
    assign: async (item, memberId) => {
      setBusyItem(item.id);
      try {
        await axios.put(
          `${API}/vexos-checklist/client/${clToken}/items/${item.id}/assignee`,
          { member_id: memberId || null },
        );
        await refresh();
        if (memberId) toast.success("Sent — they've got the item and the link.");
        return true;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't pass that on. Please try again.");
        return false;
      } finally {
        setBusyItem(null);
      }
    },
    removeLink: async (item, linkId) => {
      setBusyItem(item.id);
      try {
        await axios.delete(
          `${API}/vexos-checklist/client/${clToken}/items/${item.id}/links/${linkId}`,
        );
        await refresh();
      } catch (e) {
        toast.error("Couldn't remove that link.");
      } finally {
        setBusyItem(null);
      }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [busyItem]);

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <p className="micro-label">Loading your project</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-6">
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <p className="micro-label" style={{ marginBottom: 14 }}>Project dashboard</p>
          <h1 className="heading-2">This link isn't available</h1>
          <p className="body-text" style={{ marginTop: 12 }}>{error}</p>
        </div>
      </div>
    );
  }

  const firstName = (profile.name || "").trim().split(" ")[0];
  const onDashboard = page === "dashboard";

  /* Land on the list that still owes us something, so a client who clicks
     through from the dashboard sees work rather than a finished list. */
  const trackerLists = tracker.checklists || [];
  const defaultTodoKind = (
    trackerLists.find((l) => (l.required_complete || 0) < (l.required_total || 0)) || trackerLists[0] || {}
  ).kind;

  const openTodo = (k) => {
    if (k) setTodoKind(k);
    setPage("todo");
  };

  /* The banner's Pay button and the Pay button on any milestone row open the
     same dialog, against the same server-derived amount. */
  const openPay = (milestoneId) => {
    const m = (tracker.payments?.milestones || []).find((x) => x.id === milestoneId);
    if (m) setPayMilestone(m);
    else toast.error("We couldn't find that payment. Please refresh and try again.");
  };

  return (
    <div className="paper-wash min-h-screen bg-canvas">
      <Toaster position="top-center" />

      <Sidebar page={page} setPage={setPage} tracker={tracker} />

      {/* The rail is fixed, so the content is inset by its width rather than
          sitting beside it, and centres in what is left. 1328 = the old 1560
          column minus the rail, so the reading measure is unchanged. */}
      <div className="lg:ml-[232px]">
        <div className="flex min-w-0" style={{ maxWidth: 1328, margin: "0 auto", gap: 36, padding: "0 clamp(20px, 3vw, 44px)" }}>
          {/* Main column */}
          <main className="flex-1 min-w-0" style={{ paddingTop: 30, paddingBottom: 90 }}>
            {/* Above the welcome, and on EVERY page — a client who wandered off
                to Documents still needs to know their project is paused. What it
                says is decided server-side so the PM panel can preview the exact
                wording before a client reads it. */}
            <HeroBanner banner={tracker.banner} onPay={openPay} />

            {/* Header: dynamic welcome + the bell. No search, nothing to hunt for. */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 animate-reveal-up">
                <div className="lg:hidden" style={{ marginBottom: 18 }}>
                  <img src="/trp-logo.png" alt="The Render Pros"
                    style={{ height: 22, objectFit: "contain", filter: "invert(1)", mixBlendMode: "multiply" }} />
                </div>
                {/* On `lg` and up the rail already names the client and the
                    project, so this would be the same line twice. */}
                <p className="micro-label lg:hidden" style={{ marginBottom: 12 }}>
                  {tracker.client_name || "Project dashboard"}
                </p>
                <h1 style={{
                  fontSize: "clamp(26px, 3.4vw, 38px)", fontWeight: 300,
                  letterSpacing: "-1.2px", lineHeight: 1.12, color: "var(--ink)",
                }}>
                  {onDashboard ? <>Welcome to the {tracker.program_name} dashboard.</> : PAGE_TITLES[page]}
                </h1>
                {onDashboard && (
                  <p className="body-text" style={{ marginTop: 10 }}>
                    {firstName ? `Hello ${firstName} — here's` : "Here's"} where your project stands today.
                  </p>
                )}
              </div>
              <div className="shrink-0 animate-reveal-up" style={{ animationDelay: "160ms", paddingTop: 2 }}>
                <NotificationsBell tracker={tracker} token={token} />
              </div>
            </div>

            <div style={{ marginTop: 22 }}>
              <MobileNav page={page} setPage={setPage} />
            </div>

            <div key={page}>
              {page === "dashboard" && (
                <DashboardPage tracker={tracker} openTodo={openTodo} onPay={openPay}
                  highlight={deliveryFocus} />
              )}
              {page === "todo" && (
                <ClientToDoPage
                  tracker={tracker}
                  checklists={checklists}
                  opsFor={opsFor}
                  kind={todoKind || defaultTodoKind}
                  setKind={setTodoKind}
                  onOpenTeam={() => setPage("team")}
                />
              )}
              {page === "team" && (
                <TeamPage tracker={tracker} token={token} checklists={checklists}
                  refresh={refresh} openTodo={openTodo} />
              )}
              {page === "meetings" && <MeetingsPage tracker={tracker} />}
              {page === "documents" && <DocumentsPage tracker={tracker} token={token} />}
              {page === "settings" && (
                <SettingsPage tracker={tracker} token={token} profile={profile} setProfile={setProfile} setPage={setPage} />
              )}

              {/* The right rail's content still matters on small screens — it joins
                  the flow at the end of the dashboard. */}
              {onDashboard && (
                <div className="xl:hidden" style={{ marginTop: 52 }}>
                  <hr className="hairline-rule" />
                  <div style={{ paddingTop: 26 }}>
                    <RightRail tracker={tracker} setPage={setPage} />
                  </div>
                </div>
              )}
            </div>
          </main>

          {/* Right rail */}
          <aside className="hidden xl:block shrink-0" style={{ width: 324, paddingTop: 30, paddingBottom: 90 }}>
            <div className="animate-reveal-up" style={{ animationDelay: "240ms" }}>
              <RightRail tracker={tracker} setPage={setPage} />
            </div>
          </aside>
        </div>
      </div>

      {payMilestone && (
        <PayDialog
          milestone={payMilestone}
          token={token}
          onClose={() => setPayMilestone(null)}
        />
      )}
    </div>
  );
}
